import * as React from "react";

import { api } from "@repo/backend";
import { isEditorialRoleHidden } from "@repo/shared/media-day/editorial-roles";
import { voxisPersonVariants } from "@repo/shared/media-day/voxis-verification";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@repo/ui/components/accordion";
import { Badge } from "@repo/ui/components/badge";
import { Button } from "@repo/ui/components/button";
import { Card, CardHeader, CardTitle } from "@repo/ui/components/card";
import { Checkbox } from "@repo/ui/components/checkbox";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@repo/ui/components/context-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/components/dialog";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@repo/ui/components/hover-card";
import { Input } from "@repo/ui/components/input";
import { Table, TableBody, TableCell, TableRow } from "@repo/ui/components/table";
import { Textarea } from "@repo/ui/components/textarea";
import { cn } from "@repo/ui/lib/utils";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import {
  Camera,
  Check,
  Clock,
  CheckCircle2,
  CircleHelp,
  Coffee,
  Crop,
  Crown,
  Download,
  Images,
  Pencil,
  Scissors,
  StickyNote,
  Trash2,
  Upload,
  UserX,
} from "lucide-react";
import { toast } from "sonner";

import { MediaDayAdminSettingsSheet } from "@/components/media-day/admin-settings-sheet";
import {
  MediaDayStatusLanguageProvider,
  MediaDayStatusLanguageSwitch,
  useMediaDayStatusLanguage,
} from "@/components/media-day/status-language";
import {
  VerificationCell,
  type VerificationSummary,
} from "@/components/media-day/verification-cell";
const padTime = (value: number) => value.toString().padStart(2, "0");
const minutesToTime = (minutes: number | null) => {
  if (minutes === null) return "";
  const safeMinutes = ((minutes % 1440) + 1440) % 1440;
  return `${padTime(Math.floor(safeMinutes / 60))}:${padTime(safeMinutes % 60)}`;
};
const timeToMinutes = (value: string) => {
  if (!value) return null;
  const [hours, minutes] = value.split(":").map(Number);
  return Number.isFinite(hours) && Number.isFinite(minutes) ? hours * 60 + minutes : null;
};
const addScheduleMinutes = (minutes: number | null, amount: number) =>
  minutes === null ? null : minutes + amount;
const formatScheduleTime = (minutes: number | null) => minutesToTime(minutes) || "—";

export const validateMediaDayStatusSearch = (search: Record<string, unknown>) => ({
  tab: search.tab === "editorial" ? ("editorial" as const) : ("cutout" as const),
  set: typeof search.set === "string" ? search.set : undefined,
  assignee: typeof search.assignee === "string" ? search.assignee : undefined,
});

export type MediaDayStatusSearch = ReturnType<typeof validateMediaDayStatusSearch>;
type BoardConfig = NonNullable<(typeof api.mediaDayStatus.getPublicBoardConfig)["_returnType"]>;
type BoardScheduleResult = NonNullable<
  (typeof api.mediaDayStatus.getPublicBoardSchedule)["_returnType"]
>;
type BoardRuntime = NonNullable<(typeof api.mediaDayStatus.getPublicBoardRuntime)["_returnType"]>;
type BoardVerification = NonNullable<
  (typeof api.mediaDayStatus.getPublicBoardVerification)["_returnType"]
>;
type BoardVerificationSummary = VerificationSummary & {
  complete: boolean;
  updatedAt: number;
  lastCheckedAt: number | null;
};
type CachedBoardRow = BoardScheduleResult["rows"][number];
type CachedPlayerRow = CachedBoardRow["players"][number];
type PlayerRow = Omit<CachedPlayerRow, "automaticVerification" | "statusUpdatedAt"> & {
  automaticVerification: {
    sourceJpg: BoardVerificationSummary | null;
    cutout: BoardVerificationSummary | null;
    cropped: BoardVerificationSummary | null;
    editorialSource: BoardVerificationSummary | null;
    editorialEdited: BoardVerificationSummary | null;
  };
  statusUpdatedAt: {
    shot?: number;
    "uploaded-jpgs"?: number;
    edited?: number;
    checked?: number;
    "uploaded-raws"?: number;
    "cut-out"?: number;
    cropped?: number;
    teamcomp?: number;
  };
};
type BoardRow = Omit<
  CachedBoardRow,
  "automaticVerification" | "players" | "teamStatusUpdatedAt"
> & {
  automaticVerification: {
    teamcomp: BoardVerificationSummary | null;
    editorialSource: BoardVerificationSummary | null;
    editorialEdited: BoardVerificationSummary | null;
  };
  players: PlayerRow[];
  teamStatusUpdatedAt: { teamcomp?: number };
};
type BoardSchedule = Omit<BoardScheduleResult, "rows"> & { rows: BoardRow[] };
type Board = BoardConfig &
  BoardSchedule & {
    folderLinks: BoardConfig["folderLinks"] & {
      automatic: BoardVerification["folderLinks"];
    };
  };
type BoardBreak = BoardSchedule["breaks"][number];
type BoardSlot = BoardSchedule["slots"][number];
const verifiedAt = (result: BoardVerificationSummary | null) =>
  result?.complete ? (result.lastSuccessfulCheckAt ?? undefined) : undefined;
type PublicScheduleItem = { kind: "break"; item: BoardBreak } | { kind: "slot"; item: BoardSlot };
type WorkflowKind = BoardRow["setKind"];
type ScheduleSettings = Board["scheduleSettings"] & {
  setKind?: WorkflowKind;
};
type Step = {
  key: string;
  label: React.ReactNode;
  icon: React.ComponentType<{ className?: string }>;
  scope?: "player" | "team";
  adminOnly?: boolean;
  automatic?: boolean;
  verificationControlled?: boolean;
  applicable?: (row: BoardRow, player?: PlayerRow) => boolean;
  teamDone?: (row: BoardRow) => boolean;
  done: (row: BoardRow, player?: PlayerRow) => boolean;
};

type PublicLocalStore = {
  getQuery: (query: unknown, args: { token: string }) => BoardSchedule | null | undefined;
  setQuery: (query: unknown, args: { token: string }, value: BoardSchedule | null) => void;
};

type PublicRuntimeLocalStore = {
  getQuery: (query: unknown, args: { token: string }) => BoardRuntime | null | undefined;
  setQuery: (query: unknown, args: { token: string }, value: BoardRuntime | null) => void;
};

type PublicConfigLocalStore = {
  getQuery: (query: unknown, args: { token: string }) => BoardConfig | null | undefined;
  setQuery: (query: unknown, args: { token: string }, value: BoardConfig | null) => void;
};

const updatePublicBoard = (
  localStore: PublicLocalStore,
  token: string,
  update: (current: BoardSchedule) => BoardSchedule,
) => {
  const args = { token };
  const current = localStore.getQuery(api.mediaDayStatus.getPublicBoardSchedule, args);
  if (!current) return;
  localStore.setQuery(api.mediaDayStatus.getPublicBoardSchedule, args, update(current));
};

const updatePublicRuntimeRow = (
  localStore: PublicRuntimeLocalStore,
  token: string,
  queueEntryId: unknown,
  update: (row: BoardRuntime["rows"][number]) => BoardRuntime["rows"][number],
) => {
  const args = { token };
  const current = localStore.getQuery(api.mediaDayStatus.getPublicBoardRuntime, args);
  if (!current) return;
  localStore.setQuery(api.mediaDayStatus.getPublicBoardRuntime, args, {
    ...current,
    rows: current.rows.map((row) =>
      String(row.queueEntryId) === String(queueEntryId) ? update(row) : row,
    ),
  });
};

const updatePublicConfig = (
  localStore: PublicConfigLocalStore,
  token: string,
  update: (current: BoardConfig) => BoardConfig,
) => {
  const args = { token };
  const current = localStore.getQuery(api.mediaDayStatus.getPublicBoardConfig, args);
  if (!current) return;
  localStore.setQuery(api.mediaDayStatus.getPublicBoardConfig, args, update(current));
};

const updateBoardRow = (
  board: BoardSchedule,
  queueEntryId: unknown,
  update: (row: BoardRow) => BoardRow,
) => ({
  ...board,
  rows: board.rows.map((row) =>
    String(row.queueEntryId) === String(queueEntryId) ? update(row) : row,
  ),
});

const updateBoardPlayer = (
  board: BoardSchedule,
  playerId: unknown,
  update: (player: PlayerRow, row: BoardRow) => PlayerRow,
) => ({
  ...board,
  rows: board.rows.map((row) => ({
    ...row,
    players: row.players.map((player) =>
      String(player.id) === String(playerId) ? update(player, row) : player,
    ),
  })),
});

const mergePublicBoardRuntime = (
  schedule: BoardScheduleResult,
  runtime: BoardRuntime,
): BoardScheduleResult => {
  const runtimeByQueueEntry = new Map(runtime.rows.map((row) => [String(row.queueEntryId), row]));
  return {
    ...schedule,
    rows: schedule.rows.map((row) => {
      const state = runtimeByQueueEntry.get(String(row.queueEntryId));
      if (!state) return row;
      const shotPlayerIds = new Set(state.shotPlayerIds.map(String));
      const shotAtByPlayer = new Map(
        state.shotAtByPlayer.map((shot) => [String(shot.playerId), shot.scannedAt]),
      );
      return {
        ...row,
        etaMinutes: state.etaMinutes,
        delayMinutes: state.delayMinutes,
        playersShot: row.players.filter(
          (player) => !player.isNoShow && shotPlayerIds.has(String(player.id)),
        ).length,
        shot: state.isDone,
        edited: state.isEdited || row.edited,
        cropped: state.isCropped || row.cropped,
        players: row.players.map((player) => ({
          ...player,
          statuses: {
            ...player.statuses,
            shot: !player.isNoShow && shotPlayerIds.has(String(player.id)),
          },
          statusUpdatedAt: {
            ...player.statusUpdatedAt,
            shot: shotAtByPlayer.get(String(player.id)),
          },
          isShot: !player.isNoShow && shotPlayerIds.has(String(player.id)),
        })),
      };
    }),
  };
};

const mergePublicBoardVerification = (
  board: Board,
  verification: BoardVerification | undefined,
): Board => {
  if (!verification) return board;

  const verificationByKey = new Map(
    verification.targets.map((target) => [target.key, target.verification]),
  );
  const getVerification = (queueEntryId: unknown, subjectId: unknown, stage: string) =>
    verificationByKey.get(`${String(queueEntryId)}:${String(subjectId)}:${stage}`) ?? null;
  return {
    ...board,
    folderLinks: { ...board.folderLinks, automatic: verification.folderLinks },
    rows: board.rows.map((row) => {
      const teamcomp =
        row.participantMode === "individual"
          ? null
          : getVerification(row.queueEntryId, row.teamId, "teamcomp");
      const editorialSource = getVerification(row.queueEntryId, row.teamId, "editorialSource");
      const editorialEdited = getVerification(row.queueEntryId, row.teamId, "editorialEdited");

      return {
        ...row,
        teamStatuses: { teamcomp: teamcomp?.complete === true },
        teamStatusUpdatedAt: { teamcomp: verifiedAt(teamcomp) },
        automaticVerification: { teamcomp, editorialSource, editorialEdited },
        players: row.players.map((player) => {
          const sourceJpg = getVerification(row.queueEntryId, player.id, "sourceJpg");
          const cutout = getVerification(row.queueEntryId, player.id, "cutout");
          const cropped = getVerification(row.queueEntryId, player.id, "cropped");
          const playerEditorialSource = getVerification(
            row.queueEntryId,
            player.id,
            "editorialSource",
          );
          const playerEditorialEdited = getVerification(
            row.queueEntryId,
            player.id,
            "editorialEdited",
          );

          return {
            ...player,
            verificationExpectedFiles:
              row.setKind === "editorial"
                ? player.verificationExpectedFiles
                : voxisPersonVariants({
                    role: player.role ?? undefined,
                    isCaptain: player.isCaptain,
                    substitutesFull: verification.config?.substitutesFull,
                  }).length *
                    3 +
                  (row.participantMode === "individual" || row.isSolo ? 0 : 1),
            automaticVerification: {
              sourceJpg,
              cutout,
              cropped,
              editorialSource: playerEditorialSource,
              editorialEdited: playerEditorialEdited,
            },
            statuses: {
              ...player.statuses,
              "uploaded-jpgs":
                row.setKind === "editorial"
                  ? playerEditorialSource?.complete === true
                  : player.statuses["uploaded-jpgs"],
              edited:
                row.setKind === "editorial"
                  ? playerEditorialEdited?.complete === true
                  : player.statuses.edited,
              "uploaded-raws": sourceJpg?.complete === true,
              "cut-out": cutout?.complete === true,
              cropped: cropped?.complete === true,
              teamcomp: teamcomp?.complete === true,
            },
            statusUpdatedAt: {
              ...player.statusUpdatedAt,
              "uploaded-jpgs":
                row.setKind === "editorial"
                  ? verifiedAt(playerEditorialSource)
                  : player.statusUpdatedAt["uploaded-jpgs"],
              edited:
                row.setKind === "editorial"
                  ? verifiedAt(playerEditorialEdited)
                  : player.statusUpdatedAt.edited,
              "uploaded-raws": verifiedAt(sourceJpg),
              "cut-out": verifiedAt(cutout),
              cropped: verifiedAt(cropped),
              teamcomp: verifiedAt(teamcomp),
            },
          };
        }),
      };
    }),
  };
};

type MediaDayStatusBoardProps = {
  token: string;
  search: MediaDayStatusSearch;
};

export function PublicMediaDayStatusBoard(props: MediaDayStatusBoardProps) {
  return <MediaDayStatusBoard {...props} isAdmin={false} />;
}

export function AdminMediaDayStatusBoard(props: MediaDayStatusBoardProps) {
  return <MediaDayStatusBoard {...props} isAdmin />;
}

function MediaDayStatusBoard({
  token,
  search,
  isAdmin,
}: MediaDayStatusBoardProps & { isAdmin: boolean }) {
  return (
    <MediaDayStatusLanguageProvider>
      <StatusBoard token={token} search={search} isAdmin={isAdmin} />
    </MediaDayStatusLanguageProvider>
  );
}

function StatusBoard({
  token,
  search,
  isAdmin,
}: {
  token: string;
  search: MediaDayStatusSearch;
  isAdmin: boolean;
}) {
  const { t } = useMediaDayStatusLanguage();
  const navigate = useNavigate({ from: "/media-day/status/$token" });
  const navigateSearch = (nextSearch: MediaDayStatusSearch) =>
    navigate({ search: nextSearch, replace: true });
  const updateArrivalTime = useMutation(
    api.mediaDayStatus.updatePublicArrivalTime,
  ).withOptimisticUpdate((localStore, args) =>
    updatePublicRuntimeRow(
      localStore as PublicRuntimeLocalStore,
      token,
      args.queueEntryId,
      (row) => ({ ...row, etaMinutes: args.etaMinutes }),
    ),
  );
  const updateTeamSlot = useMutation(api.ipad.updateIpadTeamSlot).withOptimisticUpdate(
    (localStore, args) =>
      updatePublicBoard(localStore as PublicLocalStore, token, (board) => ({
        ...board,
        slots: board.slots.map((slot) =>
          String(slot.id) === String(args.slotId)
            ? {
                ...slot,
                ...(args.etaMinutes !== undefined ? { etaMinutes: args.etaMinutes } : {}),
                ...(args.editorText !== undefined ? { editorText: args.editorText.trim() } : {}),
                ...(args.cropperText !== undefined ? { cropperText: args.cropperText.trim() } : {}),
              }
            : slot,
        ),
      })),
  );
  const updateRoleVisibility = useMutation(
    api.mediaDayStatus.updatePublicRoleVisibility,
  ).withOptimisticUpdate((localStore, args) =>
    updatePublicConfig(localStore as PublicConfigLocalStore, token, (board) => ({
      ...board,
      showAllRoles: args.showAllRoles,
    })),
  );
  const updateScheduleSettings = useMutation(
    api.mediaDayStatus.updatePublicScheduleSettings,
  ).withOptimisticUpdate((localStore, args) =>
    updatePublicConfig(localStore as PublicConfigLocalStore, token, (board) => ({
      ...board,
      scheduleSettingsBySetKey: {
        ...board.scheduleSettingsBySetKey,
        [args.setKey]: {
          setKind: args.setKind,
          teamGapMinutes:
            args.teamGapMinutes ??
            board.scheduleSettingsBySetKey?.[args.setKey]?.teamGapMinutes ??
            board.scheduleSettings.teamGapMinutes,
          picturesAvailableDelayMinutes:
            args.picturesAvailableDelayMinutes ??
            board.scheduleSettingsBySetKey?.[args.setKey]?.picturesAvailableDelayMinutes ??
            board.scheduleSettings.picturesAvailableDelayMinutes,
          editDeadlineDelayMinutes:
            args.editDeadlineDelayMinutes ??
            board.scheduleSettingsBySetKey?.[args.setKey]?.editDeadlineDelayMinutes ??
            board.scheduleSettings.editDeadlineDelayMinutes,
        },
      },
    })),
  );
  const config = useQuery(api.mediaDayStatus.getPublicBoardConfig, { token });
  const schedule = useQuery(api.mediaDayStatus.getPublicBoardSchedule, { token });
  const runtime = useQuery(api.mediaDayStatus.getPublicBoardRuntime, { token });
  const baseBoard =
    config && schedule && runtime
      ? {
          ...config,
          ...mergePublicBoardRuntime(schedule, runtime),
          folderLinks: { ...config.folderLinks, automatic: [] },
        }
      : null;
  const activeTab: WorkflowKind = search.tab === "editorial" ? "editorial" : "cutout";
  const baseRows = baseBoard?.rows ?? [];
  const baseEditorialRows = getRowsForKind(baseRows, "editorial", baseBoard?.showAllRoles);
  const baseCutoutRows = getRowsForKind(baseRows, "cutout");
  const baseWorkflowRows = [...baseCutoutRows, ...baseEditorialRows];
  const baseSets = getSetTabs(baseWorkflowRows, baseBoard?.slots ?? []);
  const baseAssignee = search.assignee?.trim();
  const baseMatchingSets = baseAssignee
    ? baseSets.filter(
        (set) =>
          filterRowsByAssignee(filterRowsBySet(baseWorkflowRows, set.key), baseAssignee).length,
      )
    : baseSets;
  const baseFallbackSet =
    baseMatchingSets.find((set) => set.kind === activeTab) ?? baseMatchingSets[0] ?? baseSets[0];
  const baseRequestedSet = baseSets.find((set) => set.key === search.set);
  const baseSelectedSet =
    baseRequestedSet &&
    (!baseAssignee || baseMatchingSets.some((set) => set.key === baseRequestedSet.key))
      ? baseRequestedSet
      : baseFallbackSet;
  const baseActiveRows = baseSelectedSet
    ? filterRowsBySet(
        baseSelectedSet.kind === "editorial" ? baseEditorialRows : baseCutoutRows,
        baseSelectedSet.key,
      )
    : [];
  const verification = useQuery(
    api.mediaDayStatus.getPublicBoardVerification,
    baseBoard && baseActiveRows.length
      ? {
          token,
          queueEntryIds: baseActiveRows.map((row) => row.queueEntryId),
        }
      : "skip",
  );
  const board = baseBoard
    ? mergePublicBoardVerification(baseBoard as Board, verification ?? undefined)
    : null;
  const rows = board?.rows ?? [];
  const editorialRows = getRowsForKind(rows, "editorial", board?.showAllRoles);
  const cutoutRows = getRowsForKind(rows, "cutout");

  if (config === undefined || schedule === undefined || runtime === undefined) {
    return (
      <main className="flex min-h-svh items-center justify-center bg-background p-6 text-foreground">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t("Loading Media Day status…")}</CardTitle>
          </CardHeader>
        </Card>
      </main>
    );
  }

  if (!board) {
    return (
      <main className="flex min-h-svh items-center justify-center bg-background p-6 text-foreground">
        <Card className="w-full max-w-md">
          <CardHeader>
            <Badge variant="outline" className="w-fit">
              {t("Link unavailable")}
            </Badge>
            <CardTitle>{t("Media Day status link is not active.")}</CardTitle>
          </CardHeader>
        </Card>
      </main>
    );
  }

  const workflowRows = [...cutoutRows, ...editorialRows];
  const sets = getSetTabs(workflowRows, board.slots);
  const assignee = search.assignee?.trim();
  const matchingSets = assignee
    ? sets.filter(
        (set) => filterRowsByAssignee(filterRowsBySet(workflowRows, set.key), assignee).length,
      )
    : sets;
  const fallbackSet =
    matchingSets.find((set) => set.kind === activeTab) ?? matchingSets[0] ?? sets[0];
  const requestedSet = sets.find((set) => set.key === search.set);
  const selectedSet =
    requestedSet && (!assignee || matchingSets.some((set) => set.key === requestedSet.key))
      ? requestedSet
      : fallbackSet;
  const activeSetKey = selectedSet?.key ?? "all";
  const activeKind = selectedSet?.kind ?? activeTab;
  const activeParticipantMode = selectedSet?.participantMode ?? "team";
  const activeKindRows = activeKind === "editorial" ? editorialRows : cutoutRows;
  const activeSetRows = selectedSet
    ? filterRowsBySet(activeKindRows, activeSetKey)
    : activeKindRows;
  const visibleRows = assignee ? filterRowsByAssignee(activeSetRows, assignee) : activeSetRows;
  const activeSetBreaks = board.breaks.filter(
    (item) => item.setKind === activeKind && setKey(item) === activeSetKey,
  );
  const activeSetSlots = board.slots.filter(
    (item) => item.setKind === activeKind && setKey(item) === activeSetKey,
  );
  const savedScheduleSettings = board.scheduleSettingsBySetKey?.[activeSetKey];
  const scheduleSettings: ScheduleSettings = {
    ...board.scheduleSettings,
    ...savedScheduleSettings,
    setKind: savedScheduleSettings?.setKind ?? activeKind,
  };
  const setAssignee = (name?: string) => {
    const nextAssignee = name && !isAssigneeName(assignee, name) ? name : undefined;
    const nextSet = nextAssignee
      ? sets.find(
          (set) =>
            filterRowsByAssignee(filterRowsBySet(workflowRows, set.key), nextAssignee).length,
        )
      : selectedSet;
    return navigateSearch({
      ...search,
      assignee: nextAssignee,
      tab: nextSet?.kind ?? search.tab,
      set: nextSet?.key,
    });
  };
  const setActiveSet = (set: string) => {
    const kind = sets.find((item) => item.key === set)?.kind ?? activeTab;
    return navigateSearch({
      ...search,
      tab: kind,
      set: set === "all" ? undefined : set,
    });
  };
  const scheduledItems = [
    ...activeSetRows.map((row) => ({
      kind: "team" as const,
      id: String(row.queueEntryId),
      order: row.order,
      etaMinutes: row.etaMinutes,
      row,
    })),
    ...activeSetSlots.map((slot) => ({
      kind: "slot" as const,
      id: String(slot.id),
      order: slot.order,
      etaMinutes: slot.etaMinutes,
      slot,
    })),
  ].toSorted((a, b) => a.order - b.order);
  const scheduleItemsFrom = (
    startIndex: number,
    etaMinutes: number | null,
    gap = scheduleSettings.teamGapMinutes,
  ) => {
    const itemsToUpdate = scheduledItems.slice(startIndex);
    const firstItem = itemsToUpdate[0];
    if (!firstItem || !isAdmin) return;

    void Promise.all(
      itemsToUpdate.map((item, index) => {
        const breakMinutes = activeSetBreaks.reduce(
          (total, entry) =>
            entry.order > firstItem.order && entry.order < item.order
              ? total + entry.durationMinutes
              : total,
          0,
        );
        const nextEta = etaMinutes === null ? null : etaMinutes + index * gap + breakMinutes;
        return item.kind === "team"
          ? updateArrivalTime({
              token,
              queueEntryId: item.row.queueEntryId,
              etaMinutes: nextEta,
            })
          : updateTeamSlot({ slotId: item.slot.id, etaMinutes: nextEta });
      }),
    );
  };
  const scheduleFromItem = (kind: "team" | "slot", id: string, etaMinutes: number | null) => {
    const startIndex = scheduledItems.findIndex((item) => item.kind === kind && item.id === id);
    if (startIndex >= 0) scheduleItemsFrom(startIndex, etaMinutes);
  };

  return (
    <main className="min-h-svh bg-neutral-100 px-3 pb-3 text-neutral-950 md:px-4 md:pb-4">
      <div className="mx-auto flex max-w-[1800px] flex-col">
        <div className="flex flex-col gap-3 bg-neutral-100 pt-3 md:sticky md:top-0 md:z-40 md:pt-4">
          <MediaDayDateHeader
            date={board.date}
            adminSettings={
              <div className="ml-auto flex shrink-0 items-center gap-2">
                <MediaDayStatusLanguageSwitch />
                {isAdmin ? (
                  <MediaDayAdminSettingsSheet
                    activeSet={selectedSet ?? null}
                    settings={scheduleSettings}
                    firstEtaMinutes={scheduledItems[0]?.etaMinutes ?? null}
                    hasScheduleItems={scheduledItems.length > 0}
                    playerCount={activeSetRows.reduce(
                      (total, row) => total + getDeadlinePlayerCount(row),
                      0,
                    )}
                    editorial={activeKind === "editorial"}
                    showAllRoles={board.showAllRoles}
                    onShowAllRolesChange={(showAllRoles) =>
                      updateRoleVisibility({ token, showAllRoles })
                    }
                    onSettingsChange={(settings) =>
                      updateScheduleSettings({
                        token,
                        setKey: activeSetKey,
                        setKind: activeKind,
                        ...settings,
                      })
                    }
                    onApplySchedule={(etaMinutes, gapMinutes) =>
                      scheduleItemsFrom(0, etaMinutes, gapMinutes)
                    }
                  />
                ) : null}
              </div>
            }
          />
          <AssigneeWorkloadSummary
            rows={workflowRows}
            selectedAssignee={assignee}
            onSelectAssignee={setAssignee}
          />
          <BoardNavigation
            sets={sets}
            activeSetKey={activeSetKey ?? "all"}
            selectedAssignee={assignee}
            onSelectSet={setActiveSet}
          />
          <StatusCounters kind={activeKind} rows={visibleRows} folderLinks={board.folderLinks} />
          <div className="hidden md:block">
            <ColumnHeaderBar kind={activeKind} participantMode={activeParticipantMode} />
          </div>
        </div>

        <div className="overflow-x-auto rounded-md border border-neutral-900 bg-white shadow-sm md:overflow-hidden md:rounded-t-none md:rounded-b-md md:border-t-0">
          <StatusTable
            token={token}
            kind={activeKind}
            participantMode={activeParticipantMode}
            rows={visibleRows}
            positionRows={activeSetRows}
            breaks={activeSetBreaks}
            slots={activeSetSlots}
            canEditAdminStatuses={isAdmin}
            canEditRemoteEditors={isAdmin}
            canViewStatusHistory={isAdmin}
            onScheduleFromRow={(row, etaMinutes) =>
              scheduleFromItem("team", String(row.queueEntryId), etaMinutes)
            }
            onScheduleFromSlot={(slot, etaMinutes) =>
              scheduleFromItem("slot", String(slot.id), etaMinutes)
            }
            onSlotEditorChange={(slot, editorText) =>
              updateTeamSlot({ slotId: slot.id, editorText })
            }
            onSlotCropperChange={(slot, cropperText) =>
              updateTeamSlot({ slotId: slot.id, cropperText })
            }
            scheduleSettings={scheduleSettings}
            selectedAssignee={assignee}
            onClearAssignee={() => setAssignee(undefined)}
          />
        </div>
      </div>
    </main>
  );
}

type WorkloadProgress = { done: number; total: number };
type SetAssigneeProgress = { editor?: WorkloadProgress; cropper?: WorkloadProgress };
type SetTab = {
  key: string;
  label: string;
  kind: WorkflowKind;
  gameNames: string[];
  participantMode: "team" | "individual";
  teams: number;
  done: number;
  assigneeProgress: Record<string, SetAssigneeProgress>;
};

function MediaDayDateHeader({
  date,
  adminSettings,
}: {
  date: string;
  adminSettings?: React.ReactNode;
}) {
  const { locale, t } = useMediaDayStatusLanguage();
  return (
    <section className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-neutral-900 bg-white px-4 py-3 shadow-sm">
      <div>
        <div className="text-xs font-bold tracking-wide text-neutral-500 uppercase">
          {t("Media Day date")}
        </div>
        <div className="mt-0.5 text-2xl font-black tracking-tight text-neutral-950 tabular-nums">
          {formatBoardDate(date, locale)}
        </div>
      </div>
      {adminSettings}
    </section>
  );
}

function AssigneeWorkloadSummary({
  rows,
  selectedAssignee,
  onSelectAssignee,
}: {
  rows: BoardRow[];
  selectedAssignee?: string;
  onSelectAssignee: (name?: string) => void;
}) {
  const { t } = useMediaDayStatusLanguage();
  const people = getAssigneeWorkloads(rows);
  const totalAssignments = people.reduce((total, person) => total + person.overall.total, 0);

  return (
    <section className="overflow-hidden rounded-lg border border-neutral-400 bg-white shadow-sm">
      <Accordion defaultValue={[]}>
        <AccordionItem value="assignee-workload" className="border-0">
          <AccordionTrigger className="cursor-pointer items-center rounded-none border-0 bg-neutral-100 px-4 py-3 hover:bg-neutral-200 hover:no-underline aria-expanded:border-b aria-expanded:border-neutral-300 [&_[data-slot=accordion-trigger-icon]]:size-7 [&_[data-slot=accordion-trigger-icon]]:rounded-md [&_[data-slot=accordion-trigger-icon]]:border [&_[data-slot=accordion-trigger-icon]]:border-neutral-400 [&_[data-slot=accordion-trigger-icon]]:bg-white [&_[data-slot=accordion-trigger-icon]]:p-1.5 [&_[data-slot=accordion-trigger-icon]]:text-neutral-900">
            <div className="flex min-h-7 min-w-0 flex-1 items-center gap-3 pr-3">
              <span className="inline-flex h-7 shrink-0 items-center text-sm leading-none font-bold tracking-wide text-neutral-900 uppercase">
                <span>{t("Filter by person")}</span>
              </span>
              <span
                className={cn(
                  "inline-flex h-7 max-w-28 min-w-0 items-center truncate text-xs leading-none font-medium text-neutral-600 sm:max-w-none",
                  selectedAssignee &&
                    "rounded-md border border-neutral-300 bg-white px-2.5 font-semibold text-neutral-800",
                )}
              >
                {selectedAssignee ? (
                  `${t("Filtering")}: ${selectedAssignee}`
                ) : people.length ? (
                  <>
                    <span className="sm:hidden">
                      {people.length} {t("people")}
                    </span>
                    <span className="hidden sm:inline">
                      {people.length} {t("people")} · {totalAssignments} {t("photo tasks")}
                    </span>
                  </>
                ) : (
                  t("No assignments")
                )}
              </span>
              <span className="ml-auto inline-flex h-7 shrink-0 items-center rounded-md border border-neutral-400 bg-white px-2.5 text-xs leading-none font-bold text-neutral-900 shadow-sm group-aria-expanded/accordion-trigger:hidden">
                <span className="sm:hidden">{t("View")}</span>
                <span className="hidden sm:inline">{t("View details")}</span>
              </span>
              <span className="ml-auto hidden h-7 shrink-0 items-center rounded-md border border-neutral-400 bg-white px-2.5 text-xs leading-none font-bold text-neutral-900 shadow-sm group-aria-expanded/accordion-trigger:inline-flex">
                <span className="sm:hidden">{t("Hide")}</span>
                <span className="hidden sm:inline">{t("Hide details")}</span>
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-0">
            {selectedAssignee ? (
              <div className="flex items-center gap-2 border-t border-neutral-200 px-3 py-2">
                <Badge className="bg-neutral-950 text-white hover:bg-neutral-950">
                  {t("Filtering")}: {selectedAssignee}
                </Badge>
                <Button
                  size="sm"
                  variant="outline"
                  className="bg-white text-neutral-950 hover:bg-neutral-100 hover:text-neutral-950"
                  onClick={() => onSelectAssignee(undefined)}
                >
                  {t("Clear filter")}
                </Button>
              </div>
            ) : null}
            {people.length ? (
              <div className="max-h-[222px] overflow-auto">
                <table className="w-full text-left text-sm">
                  <thead className="sticky top-0 z-10 bg-white text-xs font-semibold text-neutral-500 uppercase">
                    <tr className="border-y border-neutral-200">
                      <th className="px-3 py-2">{t("Person")}</th>
                      <th className="px-3 py-2 text-right">{t("Cutout")}</th>
                      <th className="px-3 py-2 text-right">{t("Editorial")}</th>
                      <th className="px-3 py-2 text-right">{t("Cropper")}</th>
                      <th className="px-3 py-2 text-right">{t("Overall")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {people.map((person) => (
                      <tr
                        key={person.key}
                        className={cn(
                          "border-b border-neutral-100 last:border-0 hover:bg-neutral-50",
                          isAssigneeName(selectedAssignee, person.name) &&
                            "bg-neutral-200 hover:bg-neutral-200",
                        )}
                      >
                        <td className="px-3 py-2 font-semibold text-neutral-950">
                          <button
                            type="button"
                            aria-pressed={isAssigneeName(selectedAssignee, person.name)}
                            className="w-full text-left underline-offset-2 hover:underline"
                            onClick={() => onSelectAssignee(person.name)}
                          >
                            {person.name}
                          </button>
                        </td>
                        <WorkloadCell progress={person.cutoutEditor} />
                        <WorkloadCell progress={person.editorialEditor} />
                        <WorkloadCell progress={person.cropper} />
                        <td className="px-3 py-2 text-right">
                          <div className="font-semibold tabular-nums">
                            {formatWorkload(person.overall, t)}
                          </div>
                          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-neutral-200">
                            <div
                              className="h-full bg-green-600"
                              style={{ width: `${workloadPercent(person.overall)}%` }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="border-t border-neutral-200 px-3 py-2 text-sm text-neutral-500">
                {t("No assignments yet")}
              </p>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </section>
  );
}

function WorkloadCell({ progress }: { progress: WorkloadProgress }) {
  const { t } = useMediaDayStatusLanguage();
  return <td className="px-3 py-2 text-right tabular-nums">{formatWorkload(progress, t)}</td>;
}

function getRowEditors(row: BoardRow) {
  if (row.assignedRemoteEditorDetails.length) return row.assignedRemoteEditorDetails;

  return row.remoteEditorText
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean)
    .map((name) => ({ id: `text:${name.toLowerCase()}`, name, avatar: null }));
}

function getRowAssignees(row: BoardRow) {
  const assignees = [
    ...getRowEditors(row).map((editor) => ({
      key: assigneeKey(editor.name),
      name: editor.name,
      role: "editor" as const,
    })),
    ...row.cropperText
      .split(",")
      .map((name) => name.trim())
      .filter(Boolean)
      .map((name) => ({ key: assigneeKey(name), name, role: "cropper" as const })),
  ];

  return [...new Map(assignees.map((person) => [`${person.role}:${person.key}`, person])).values()];
}

function getAssigneeWorkloads(rows: BoardRow[]) {
  return [
    ...rows
      .reduce((acc, row) => {
        for (const assignee of getRowAssignees(row)) {
          const current = acc.get(assignee.key) ?? {
            key: assignee.key,
            name: assignee.name,
            cutoutEditor: { done: 0, total: 0 },
            editorialEditor: { done: 0, total: 0 },
            cropper: { done: 0, total: 0 },
            overall: { done: 0, total: 0 },
          };
          const progress = getRowRolePhotoProgress(row, assignee.role);
          const workload =
            assignee.role === "cropper"
              ? current.cropper
              : row.setKind === "editorial"
                ? current.editorialEditor
                : current.cutoutEditor;
          workload.done += progress.done;
          workload.total += progress.total;
          current.overall.done += progress.done;
          current.overall.total += progress.total;
          acc.set(assignee.key, current);
        }
        return acc;
      }, new Map<string, { key: string; name: string; cutoutEditor: WorkloadProgress; editorialEditor: WorkloadProgress; cropper: WorkloadProgress; overall: WorkloadProgress }>())
      .values(),
  ].toSorted((a, b) => b.overall.total - a.overall.total || a.name.localeCompare(b.name));
}

function assigneeKey(name: string) {
  return name.trim().toLowerCase();
}

function isAssigneeName(selected: string | undefined, name: string) {
  return Boolean(selected && assigneeKey(selected) === assigneeKey(name));
}

function filterRowsByAssignee(rows: BoardRow[], name: string) {
  const key = assigneeKey(name);
  return rows.filter((row) => getRowAssignees(row).some((assignee) => assignee.key === key));
}

function getRowRolePhotoProgress(row: BoardRow, role: "editor" | "cropper") {
  const statusKey =
    role === "cropper" ? "cropped" : row.setKind === "editorial" ? "edited" : "cut-out";
  const playerProgress = row.players.reduce(
    (progress, player) => {
      const photos = expectedPlayerPhotos(row.setKind, row, player);
      progress.total += photos;
      if (player.statuses[statusKey]) progress.done += photos;
      return progress;
    },
    { done: 0, total: 0 },
  );
  const attendingPlayers = row.players.filter((player) => !player.isNoShow);
  const hasTeamPhoto =
    role === "editor" && row.setKind === "editorial" && !row.isSolo && attendingPlayers.length > 0;
  if (!hasTeamPhoto) return playerProgress;

  return {
    done:
      playerProgress.done + (attendingPlayers.every((player) => player.statuses.edited) ? 1 : 0),
    total: playerProgress.total + 1,
  };
}

function isRowRoleDone(row: BoardRow, role: "editor" | "cropper") {
  const attendingPlayers = row.players.filter((player) => !player.isNoShow);
  if (!attendingPlayers.length) return false;
  if (role === "cropper") return attendingPlayers.every((player) => player.statuses.cropped);
  const key = row.setKind === "editorial" ? "edited" : "cut-out";
  return attendingPlayers.every((player) => player.statuses[key]);
}

function formatWorkload(progress: WorkloadProgress, t: (phrase: string) => string) {
  if (!progress.total) return "—";
  return `${progress.done}/${progress.total} ${t("photos done")}`;
}

function workloadPercent(progress: WorkloadProgress) {
  return progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
}

function formatSetAssigneeProgress(progress: SetAssigneeProgress, t: (phrase: string) => string) {
  return [
    progress.editor ? `${t("Editor")} ${progress.editor.done}/${progress.editor.total}` : null,
    progress.cropper ? `${t("Cropper")} ${progress.cropper.done}/${progress.cropper.total}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

function BoardNavigation({
  sets,
  activeSetKey,
  selectedAssignee,
  onSelectSet,
}: {
  sets: SetTab[];
  activeSetKey: string;
  selectedAssignee?: string;
  onSelectSet: (set: string) => void;
}) {
  const { t } = useMediaDayStatusLanguage();
  if (sets.length <= 1) return null;

  return (
    <div className="grid gap-2">
      <StatusTabGrid label={t("Photographic Studios")}>
        {sets.map((set) => {
          const progress = selectedAssignee
            ? set.assigneeProgress?.[assigneeKey(selectedAssignee)]
            : undefined;
          const disabled = Boolean(selectedAssignee && !progress);
          return (
            <StatusTabButton
              key={set.key}
              active={set.key === activeSetKey}
              disabled={disabled}
              title={set.label}
              meta={
                progress
                  ? formatSetAssigneeProgress(progress, t)
                  : `${set.done}/${set.teams} ${t(set.participantMode === "individual" ? "players done" : "teams done")}`
              }
              detail={set.gameNames.join(", ")}
              onClick={() => onSelectSet(set.key)}
            />
          );
        })}
      </StatusTabGrid>
    </div>
  );
}

function StatusTabGrid({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="overflow-hidden rounded-lg border border-neutral-300 bg-white shadow-sm">
      <div className="border-b border-neutral-200 bg-neutral-50 px-3 py-1.5 text-xs font-bold tracking-wide text-neutral-500 uppercase">
        {label}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6">{children}</div>
    </section>
  );
}

function StatusTabButton({
  active,
  disabled,
  title,
  meta,
  detail,
  onClick,
}: {
  active: boolean;
  disabled?: boolean;
  title: string;
  meta: string;
  detail?: string;
  onClick: () => void;
}) {
  const { t } = useMediaDayStatusLanguage();
  return (
    <button
      type="button"
      aria-pressed={active}
      disabled={disabled}
      title={disabled ? t("No work for this assignee") : undefined}
      aria-label={disabled ? `${title}: ${t("No work for this assignee")}` : title}
      onClick={onClick}
      className={cn(
        "min-h-20 border-r border-b border-neutral-200 px-4 py-3 text-left transition-colors last:border-r-0 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-neutral-900 xl:border-b-0",
        active
          ? "bg-neutral-200 text-neutral-950 ring-1 ring-neutral-900 ring-inset"
          : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-950",
        disabled && "pointer-events-none bg-neutral-50 text-neutral-400 opacity-70",
      )}
    >
      <span
        className={cn(
          "block truncate text-sm font-bold text-neutral-950",
          disabled && "text-neutral-400",
        )}
      >
        {title}
      </span>
      <span className="mt-1 block text-xs font-medium">{meta}</span>
      {detail ? (
        <span
          className={cn(
            "mt-1 block truncate text-xs text-neutral-500",
            disabled && "text-neutral-400",
          )}
        >
          {detail}
        </span>
      ) : null}
    </button>
  );
}

function statusColumnWidths(kind: WorkflowKind, steps: Step[]) {
  const leading = kind === "cutout" ? [13.5, 8.1, 10.8, 12] : [15, 9, 15];
  const statusTotal = kind === "cutout" ? 55.6 : 61;
  const downloadReady = kind === "cutout" ? 12 : 16;
  const downloadReadyKey = kind === "cutout" ? "uploaded-raws" : "uploaded-jpgs";
  const remainingStatus = (statusTotal - downloadReady) / (steps.length - 1);

  return [
    ...leading,
    ...steps.map((step) => (step.key === downloadReadyKey ? downloadReady : remainingStatus)),
  ];
}

function ColumnHeaderBar({
  kind,
  participantMode,
  className,
}: {
  kind: WorkflowKind;
  participantMode: "team" | "individual";
  className?: string;
}) {
  const { t } = useMediaDayStatusLanguage();
  const steps = stepsFor(kind).filter(
    (step) => participantMode !== "individual" || step.scope !== "team",
  );
  const hasCropperColumn = kind === "cutout";
  const columnWidths = statusColumnWidths(kind, steps);

  return (
    <div
      className={cn(
        "overflow-hidden rounded-t-md border border-neutral-900 bg-black shadow-sm",
        className,
      )}
    >
      <div
        className="grid h-9 items-center text-[13px] font-semibold text-white"
        style={{ gridTemplateColumns: columnWidths.map((width) => `${width}%`).join(" ") }}
      >
        <div className="border-r border-white/15 px-2">{t("Player")}</div>
        <div className="border-r border-white/15 px-2">{t("Position")}</div>
        <div className="border-r border-white/15 px-2">{t("Editor")}</div>
        {hasCropperColumn ? (
          <div className="border-r border-white/15 px-2">{t("Cropper")}</div>
        ) : null}
        {steps.map((step) => (
          <div
            key={step.key}
            className="border-r border-white/15 px-2 text-center whitespace-nowrap last:border-r-0"
          >
            {typeof step.label === "string" ? t(step.label) : step.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusTable({
  token,
  kind,
  participantMode,
  rows,
  positionRows,
  breaks,
  slots,
  canEditAdminStatuses,
  canEditRemoteEditors,
  canViewStatusHistory,
  onScheduleFromRow,
  onScheduleFromSlot,
  onSlotEditorChange,
  onSlotCropperChange,
  scheduleSettings,
  selectedAssignee,
  onClearAssignee,
}: {
  token: string;
  kind: WorkflowKind;
  participantMode: "team" | "individual";
  rows: BoardRow[];
  positionRows: BoardRow[];
  breaks: BoardBreak[];
  slots: BoardSlot[];
  canEditAdminStatuses: boolean;
  canEditRemoteEditors: boolean;
  canViewStatusHistory: boolean;
  onScheduleFromRow: (row: BoardRow, etaMinutes: number | null) => void;
  onScheduleFromSlot: (slot: BoardSlot, etaMinutes: number | null) => void;
  onSlotEditorChange: (slot: BoardSlot, editorText: string) => Promise<unknown>;
  onSlotCropperChange: (slot: BoardSlot, cropperText: string) => Promise<unknown>;
  scheduleSettings: ScheduleSettings;
  selectedAssignee?: string;
  onClearAssignee: () => void;
}) {
  const { t } = useMediaDayStatusLanguage();
  const steps = stepsFor(kind).filter(
    (step) => participantMode !== "individual" || step.scope !== "team",
  );
  const hasCropperColumn = kind === "cutout";
  const columnWidths = statusColumnWidths(kind, steps);
  const tableColumns = columnWidths.length;
  const setTeamCaptain = useMutation(api.ipad.setTeamCaptain).withOptimisticUpdate(
    (localStore, args) =>
      updatePublicBoard(localStore as PublicLocalStore, token, (board) => ({
        ...board,
        rows: board.rows.map((row) =>
          String(row.teamId) === String(args.teamId)
            ? {
                ...row,
                players: row.players.map((player) => ({
                  ...player,
                  isCaptain: String(player.id) === String(args.playerId),
                })),
              }
            : row,
        ),
      })),
  );
  const restorePlayerAttendance = useMutation(
    api.mediaDayStatus.restorePlayerAttendance,
  ).withOptimisticUpdate((localStore, args) =>
    updatePublicBoard(localStore as PublicLocalStore, token, (board) =>
      updateBoardPlayer(board, args.playerId, (player) => ({
        ...player,
        isNoShow: false,
      })),
    ),
  );
  const markPlayerNoShow = useMutation(api.mediaDayStatus.markPlayerNoShow).withOptimisticUpdate(
    (localStore, args) =>
      updatePublicBoard(localStore as PublicLocalStore, token, (board) =>
        updateBoardPlayer(board, args.playerId, (player) => ({
          ...player,
          isNoShow: true,
        })),
      ),
  );
  const unsetTeamCaptain = useMutation(api.ipad.unsetTeamCaptain).withOptimisticUpdate(
    (localStore, args) =>
      updatePublicBoard(localStore as PublicLocalStore, token, (board) => ({
        ...board,
        rows: board.rows.map((row) =>
          String(row.teamId) === String(args.teamId)
            ? { ...row, players: row.players.map((player) => ({ ...player, isCaptain: false })) }
            : row,
        ),
      })),
  );
  const [noteTarget, setNoteTarget] = React.useState<{
    row: BoardRow;
    player: PlayerRow;
  } | null>(null);
  const plannedTeams = [
    ...positionRows.map((row) => ({ kind: "team" as const, id: String(row.queueEntryId), row })),
    ...slots.map((slot) => ({ kind: "slot" as const, id: String(slot.id), slot })),
  ].toSorted(
    (a, b) =>
      (a.kind === "team" ? a.row.order : a.slot.order) -
      (b.kind === "team" ? b.row.order : b.slot.order),
  );
  const teamPositionById = new Map(plannedTeams.map((item, index) => [item.id, index + 1]));
  const itemsBeforeRow = new Map<string, PublicScheduleItem[]>();
  const orderedRows = rows.toSorted((a, b) => a.order - b.order);
  const trailingItems: PublicScheduleItem[] = [];
  const scheduleItems: PublicScheduleItem[] = [
    ...breaks.map((item) => ({ kind: "break" as const, item })),
    ...slots.map((item) => ({ kind: "slot" as const, item })),
  ].toSorted((a, b) => a.item.order - b.item.order);
  for (const scheduleItem of scheduleItems) {
    const nextRow = orderedRows.find((row) => row.order > scheduleItem.item.order);
    if (!nextRow) {
      trailingItems.push(scheduleItem);
      continue;
    }
    const key = String(nextRow.queueEntryId);
    itemsBeforeRow.set(key, [...(itemsBeforeRow.get(key) ?? []), scheduleItem]);
  }

  const selectCaptain = async (row: BoardRow, player: PlayerRow) => {
    if (!canEditAdminStatuses || row.isSolo || player.isNoShow) return;
    await setTeamCaptain({ teamId: row.teamId, playerId: player.id });
    toast.success(`${player.adjustedName ?? player.name} — ${t("selected as captain")}.`);
  };

  const removeCaptain = async (row: BoardRow, player: PlayerRow) => {
    if (!canEditAdminStatuses || row.isSolo || !player.isCaptain) return;
    await unsetTeamCaptain({ teamId: row.teamId });
    toast.success(`${player.adjustedName ?? player.name} — ${t("is no longer captain")}.`);
  };

  const restoreAttendance = async (player: PlayerRow) => {
    if (!canEditAdminStatuses || !player.isNoShow) return;
    await restorePlayerAttendance({ token, playerId: player.id });
    toast.success(`${player.adjustedName ?? player.name} — ${t("attendance restored")}.`);
  };

  const markNoShow = async (player: PlayerRow) => {
    if (!canEditAdminStatuses || player.isNoShow) return;
    await markPlayerNoShow({ token, playerId: player.id });
    toast.success(`${player.adjustedName ?? player.name} — ${t("marked as no show")}.`);
  };

  if (!rows.length && !breaks.length && !slots.length) {
    return (
      <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
        {selectedAssignee ? (
          <div className="grid justify-items-center gap-3">
            <p>
              {t("No work found for")} {selectedAssignee} {t("in this set")}.
            </p>
            <Button
              size="sm"
              variant="outline"
              className="bg-white text-neutral-950 hover:bg-neutral-100 hover:text-neutral-950"
              onClick={onClearAssignee}
            >
              {t("Clear assignee filter")}
            </Button>
          </div>
        ) : (
          `${t("No")} ${t(kind)} ${t("entries queued for this board")}.`
        )}
      </div>
    );
  }

  return (
    <>
      <div className="sticky left-0 z-30 border-b border-neutral-300 bg-neutral-100 px-3 py-1.5 text-xs font-semibold text-neutral-700 md:hidden">
        {t("Swipe table horizontally to see all columns")} →
      </div>
      <div className="min-w-[1050px] md:min-w-0">
        <div className="md:hidden">
          <ColumnHeaderBar
            kind={kind}
            participantMode={participantMode}
            className="rounded-none border-0"
          />
        </div>
        <Table className="table-fixed border-collapse text-[13px]">
          <colgroup>
            {columnWidths.map((width, index) => (
              <col key={index} style={{ width: `${width}%` }} />
            ))}
          </colgroup>
          <TableBody>
            {groupRowsBySection(rows).map((section) => (
              <React.Fragment key={section.key}>
                {section.showHeader && (
                  <TableRow className="border-y-2 border-neutral-900 bg-neutral-900 hover:bg-neutral-900">
                    <TableCell colSpan={tableColumns} className="px-3 py-2 text-white">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-bold tracking-wide uppercase">
                            {section.label}
                          </div>
                          <div className="text-xs text-white/60">
                            {section.rows.length} {t("entries")}
                          </div>
                        </div>
                        <div className="text-xs font-semibold whitespace-nowrap text-white/70 tabular-nums">
                          {sum(section.rows, "playersShot")}/{sum(section.rows, "playersTotal")}{" "}
                          {t("players shot")}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                {groupRowsByTeam(section.rows, kind).map(
                  ({ row, players, teamcompPlayerCount }) => (
                    <React.Fragment key={row.queueEntryId}>
                      {(itemsBeforeRow.get(String(row.queueEntryId)) ?? []).map((scheduleItem) => (
                        <PublicScheduleTableRow
                          key={`${scheduleItem.kind}:${scheduleItem.item.id}`}
                          scheduleItem={scheduleItem}
                          colSpan={tableColumns}
                          position={
                            scheduleItem.kind === "slot"
                              ? (teamPositionById.get(String(scheduleItem.item.id)) ?? 0)
                              : undefined
                          }
                          canEdit={canEditAdminStatuses}
                          settings={scheduleSettings}
                          onSlotChange={onScheduleFromSlot}
                          onEditorChange={onSlotEditorChange}
                          onCropperChange={onSlotCropperChange}
                        />
                      ))}
                      {participantMode === "team" ? (
                        <TableRow className="border-y-2 border-neutral-900 bg-neutral-200 hover:bg-neutral-200">
                          <TableCell colSpan={tableColumns} className="px-2 py-1.5">
                            <div
                              className="grid items-center gap-3"
                              style={{ gridTemplateColumns: "auto 1fr auto" }}
                            >
                              <div className="truncate font-bold tracking-wide text-neutral-950 uppercase">
                                <span className="mr-2 inline-flex size-6 items-center justify-center rounded-full bg-neutral-950 text-xs font-black text-white tabular-nums">
                                  {teamPositionById.get(String(row.queueEntryId))}
                                </span>
                                {row.teamName}
                              </div>
                              <div className="justify-self-center">
                                <TeamScheduleField
                                  key={`${row.queueEntryId}:${row.etaMinutes ?? "unset"}`}
                                  row={row}
                                  canEdit={canEditAdminStatuses}
                                  settings={scheduleSettings}
                                  onChange={(etaMinutes) => onScheduleFromRow(row, etaMinutes)}
                                />
                              </div>
                              <div className="text-xs font-semibold whitespace-nowrap text-neutral-600 tabular-nums">
                                {row.playersShot}/{row.playersTotal} {t("players shot")}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : null}
                      {players.map((player, playerIndex) => (
                        <TableRow
                          key={`${row.queueEntryId}:${player.id}`}
                          className={cn(
                            "border-neutral-300 odd:bg-white even:bg-neutral-50 hover:bg-blue-50/60",
                            player.isCaptain &&
                              "bg-amber-100 odd:bg-amber-100 even:bg-amber-100 hover:bg-amber-100",
                            player.isNoShow &&
                              "bg-neutral-100 text-neutral-500 odd:bg-neutral-100 even:bg-neutral-100 hover:bg-neutral-100",
                          )}
                        >
                          <TableCell
                            className={cn(
                              "border-r border-neutral-200 px-2 py-1.5 font-medium text-neutral-950",
                              player.isCaptain && "bg-amber-100",
                            )}
                          >
                            <div className="grid gap-1">
                              <PlayerContextMenu
                                player={player}
                                canEdit={canEditAdminStatuses}
                                canEditCaptain={!row.isSolo}
                                onToggleCaptain={() =>
                                  void (player.isCaptain
                                    ? removeCaptain(row, player)
                                    : selectCaptain(row, player))
                                }
                                onAddNote={() => setNoteTarget({ row, player })}
                                onToggleNoShow={() =>
                                  void (player.isNoShow
                                    ? restoreAttendance(player)
                                    : markNoShow(player))
                                }
                                className="flex w-full items-center gap-1.5 truncate"
                              >
                                {participantMode === "individual" ? (
                                  <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-neutral-900 text-[10px] font-bold text-white tabular-nums">
                                    {teamPositionById.get(String(row.queueEntryId))}
                                  </span>
                                ) : null}
                                <PlayerName player={player} />
                              </PlayerContextMenu>
                              {participantMode === "individual" ? (
                                <div className="flex items-center gap-1.5 text-[11px] text-neutral-500">
                                  <Clock className="size-3" />
                                  <Input
                                    key={`${row.queueEntryId}:${row.etaMinutes ?? "unset"}`}
                                    type="time"
                                    defaultValue={minutesToTime(row.etaMinutes)}
                                    disabled={!canEditAdminStatuses}
                                    aria-label={`${t("Photoshoot start time")} — ${player.name}`}
                                    className="h-6 w-24 border-0 bg-transparent px-0 text-xs font-semibold shadow-none"
                                    onBlur={(event) =>
                                      onScheduleFromRow(
                                        row,
                                        timeToMinutes(event.currentTarget.value),
                                      )
                                    }
                                  />
                                  <span className="truncate">{row.teamName}</span>
                                </div>
                              ) : null}
                            </div>
                          </TableCell>
                          <TableCell
                            className={cn(
                              "border-r border-neutral-200 px-2 py-1.5 text-neutral-700",
                              player.isCaptain && "bg-amber-100",
                            )}
                          >
                            <PlayerContextMenu
                              player={player}
                              canEdit={canEditAdminStatuses}
                              canEditCaptain={!row.isSolo}
                              onToggleCaptain={() =>
                                void (player.isCaptain
                                  ? removeCaptain(row, player)
                                  : selectCaptain(row, player))
                              }
                              onAddNote={() => setNoteTarget({ row, player })}
                              onToggleNoShow={() =>
                                void (player.isNoShow
                                  ? restoreAttendance(player)
                                  : markNoShow(player))
                              }
                              className="block w-full"
                            >
                              <PlayerRole player={player} />
                            </PlayerContextMenu>
                          </TableCell>
                          <TableCell
                            className={cn(
                              "border-r border-neutral-200 bg-neutral-50 px-1.5 py-1",
                              player.isCaptain && "bg-amber-100",
                            )}
                          >
                            <RemoteEditorField
                              token={token}
                              queueEntryId={row.queueEntryId}
                              value={row.remoteEditorText}
                              disabled={!canEditRemoteEditors}
                            />
                          </TableCell>
                          {hasCropperColumn ? (
                            <TableCell
                              className={cn(
                                "border-r border-neutral-200 bg-neutral-50 px-1.5 py-1",
                                player.isCaptain && "bg-amber-100",
                              )}
                            >
                              <CropperField
                                token={token}
                                queueEntryId={row.queueEntryId}
                                value={row.cropperText}
                                disabled={!canEditRemoteEditors}
                              />
                            </TableCell>
                          ) : null}
                          {steps.map((step) => {
                            const isTeamStep = step.scope === "team";
                            const applicable = step.applicable?.(row, player) ?? true;
                            const checked = applicable && step.done(row, player);

                            if (player.isNoShow) {
                              return (
                                <TableCell
                                  key={step.key}
                                  className="border-r border-neutral-300 bg-neutral-100 p-0 text-center text-neutral-400 last:border-r-0"
                                >
                                  <div className="flex min-h-10 items-center justify-center text-sm font-semibold">
                                    —
                                  </div>
                                </TableCell>
                              );
                            }

                            if (isTeamStep && !isTeamcompPlayer(player)) {
                              return (
                                <TableCell
                                  key={step.key}
                                  className="border-r border-green-700/20 bg-neutral-100 p-0 text-center text-neutral-400 last:border-r-0"
                                >
                                  <div className="flex min-h-10 items-center justify-center text-sm font-semibold">
                                    —
                                  </div>
                                </TableCell>
                              );
                            }

                            if (isTeamStep && playerIndex > 0) return null;

                            return (
                              <TableCell
                                key={step.key}
                                rowSpan={isTeamStep ? teamcompPlayerCount : undefined}
                                className={cn(
                                  "border-r border-green-700/20 p-0 text-center last:border-r-0",
                                  !applicable
                                    ? "bg-neutral-100 text-neutral-400"
                                    : checked
                                      ? "bg-[#00e500]"
                                      : "bg-[#f29932]",
                                )}
                              >
                                {!applicable ? (
                                  <div className="flex h-full min-h-10 items-center justify-center text-xs font-semibold">
                                    N/A
                                  </div>
                                ) : step.automatic ? (
                                  <VerificationCell
                                    token={token}
                                    subject={player.adjustedName ?? player.name}
                                    expectedFiles={
                                      player.verificationExpectedFiles -
                                      (row.setKind === "editorial" && !row.isSolo && playerIndex > 0
                                        ? 2
                                        : 0)
                                    }
                                    items={
                                      (row.setKind === "editorial"
                                        ? [
                                            player.automaticVerification.editorialSource,
                                            player.automaticVerification.editorialEdited,
                                            ...(row.isSolo || playerIndex > 0
                                              ? []
                                              : [
                                                  row.automaticVerification.editorialSource,
                                                  row.automaticVerification.editorialEdited,
                                                ]),
                                          ]
                                        : [
                                            player.automaticVerification.sourceJpg,
                                            player.automaticVerification.cutout,
                                            player.automaticVerification.cropped,
                                            row.automaticVerification.teamcomp,
                                          ]) as (VerificationSummary | null)[]
                                    }
                                  />
                                ) : isTeamStep ? (
                                  <div className="flex h-full min-h-10 items-center justify-center">
                                    <TeamStatusMark
                                      token={token}
                                      row={row}
                                      step={step}
                                      checked={checked}
                                      disabled={
                                        step.verificationControlled === true ||
                                        Boolean(step.adminOnly && !canEditAdminStatuses) ||
                                        !teamcompPlayerCount
                                      }
                                      showHistory={canViewStatusHistory}
                                    />
                                  </div>
                                ) : (
                                  <StatusMark
                                    token={token}
                                    row={row}
                                    player={player}
                                    step={step}
                                    checked={checked}
                                    disabled={
                                      step.verificationControlled === true ||
                                      Boolean(step.adminOnly && !canEditAdminStatuses)
                                    }
                                    showHistory={canViewStatusHistory}
                                  />
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ),
                )}
              </React.Fragment>
            ))}
            {!rows.length && !slots.length ? (
              <TableRow>
                <TableCell colSpan={tableColumns} className="p-6 text-center text-neutral-500">
                  {selectedAssignee
                    ? `${t("No work found for")} ${selectedAssignee} ${t("in this set")}.`
                    : t("No teams queued for this set.")}
                </TableCell>
              </TableRow>
            ) : null}
            {trailingItems.map((scheduleItem) => (
              <PublicScheduleTableRow
                key={`${scheduleItem.kind}:${scheduleItem.item.id}`}
                scheduleItem={scheduleItem}
                colSpan={tableColumns}
                position={
                  scheduleItem.kind === "slot"
                    ? (teamPositionById.get(String(scheduleItem.item.id)) ?? 0)
                    : undefined
                }
                canEdit={canEditAdminStatuses}
                settings={scheduleSettings}
                onSlotChange={onScheduleFromSlot}
                onEditorChange={onSlotEditorChange}
                onCropperChange={onSlotCropperChange}
              />
            ))}
          </TableBody>
        </Table>
      </div>
      <PlayerNoteDialog
        key={noteTarget ? String(noteTarget.player.id) : "no-player-note-target"}
        token={token}
        target={noteTarget}
        onOpenChange={(open) => !open && setNoteTarget(null)}
      />
    </>
  );
}

function PublicScheduleTableRow({
  scheduleItem,
  colSpan,
  position,
  canEdit,
  settings,
  onSlotChange,
  onEditorChange,
  onCropperChange,
}: {
  scheduleItem: PublicScheduleItem;
  colSpan: number;
  position?: number;
  canEdit: boolean;
  settings: ScheduleSettings;
  onSlotChange: (slot: BoardSlot, etaMinutes: number | null) => void;
  onEditorChange: (slot: BoardSlot, editorText: string) => Promise<unknown>;
  onCropperChange: (slot: BoardSlot, cropperText: string) => Promise<unknown>;
}) {
  const { t } = useMediaDayStatusLanguage();
  if (scheduleItem.kind === "break") {
    const { item } = scheduleItem;
    return (
      <TableRow className="h-56 border-y-2 border-solid border-neutral-400 bg-neutral-100 hover:bg-neutral-100">
        <TableCell colSpan={colSpan} className="px-4 py-6">
          <div className="flex h-full min-h-44 flex-col items-center justify-center gap-2 text-center text-neutral-600">
            <Coffee className="size-7 shrink-0" />
            <span className="text-lg font-bold text-neutral-950">{item.name}</span>
            <span className="text-sm font-medium tabular-nums">
              {item.durationMinutes} {t("min break")}
            </span>
          </div>
        </TableCell>
      </TableRow>
    );
  }

  const { item } = scheduleItem;
  return (
    <TableRow className="border-y-2 border-dashed border-neutral-500 bg-neutral-200 hover:bg-neutral-200">
      <TableCell colSpan={colSpan} className="px-2 py-1.5">
        <div className="grid items-center gap-3" style={{ gridTemplateColumns: "auto 1fr auto" }}>
          <div className="truncate font-bold tracking-wide text-neutral-950 uppercase">
            <span className="mr-2 inline-flex size-6 items-center justify-center rounded-full bg-neutral-950 text-xs font-black text-white tabular-nums">
              {position}
            </span>
            {item.label}
          </div>
          <div className="flex flex-wrap items-end justify-center gap-2 justify-self-center">
            <SlotScheduleField
              key={`${item.id}:${item.etaMinutes ?? "unset"}`}
              slot={item}
              canEdit={canEdit}
              settings={settings}
              onChange={(etaMinutes) => onSlotChange(item, etaMinutes)}
            />
            <SlotAssigneeField
              label="Editor"
              value={item.editorText}
              disabled={!canEdit}
              disabledTitle="Only WorkGrid admins can edit the editor"
              onSave={(editorText) => onEditorChange(item, editorText)}
            />
            {item.setKind === "cutout" ? (
              <SlotAssigneeField
                label="Cropper"
                value={item.cropperText}
                disabled={!canEdit}
                disabledTitle="Only WorkGrid admins can edit the cropper"
                onSave={(cropperText) => onCropperChange(item, cropperText)}
              />
            ) : null}
          </div>
          <div className="text-xs font-semibold whitespace-nowrap text-neutral-500">
            {t("Team not assigned yet")}
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
}

function SlotAssigneeField({
  label,
  value,
  disabled,
  disabledTitle,
  onSave,
}: {
  label: string;
  value: string;
  disabled: boolean;
  disabledTitle: string;
  onSave: (value: string) => Promise<unknown>;
}) {
  const { t } = useMediaDayStatusLanguage();
  return (
    <label className="grid min-w-36 gap-0.5 text-[11px] font-semibold text-neutral-500">
      {t(label)}
      <RemoteTextField
        value={value}
        disabled={disabled}
        placeholder={t("Unassigned")}
        disabledTitle={t(disabledTitle)}
        onSave={onSave}
      />
    </label>
  );
}

function SlotScheduleField({
  slot,
  canEdit,
  settings,
  onChange,
}: {
  slot: BoardSlot;
  canEdit: boolean;
  settings: ScheduleSettings;
  onChange: (etaMinutes: number | null) => void;
}) {
  const { t } = useMediaDayStatusLanguage();
  const [value, setValue] = React.useState(minutesToTime(slot.etaMinutes));
  const picturesAvailable = addScheduleMinutes(
    slot.etaMinutes,
    settings.picturesAvailableDelayMinutes,
  );

  return (
    <div className="flex items-center gap-2">
      {canEdit ? (
        <label className="inline-flex h-10 min-w-34 items-center gap-2.5 rounded-md border border-neutral-400 bg-white px-3 text-xs font-semibold text-neutral-700 shadow-sm focus-within:border-neutral-900 focus-within:ring-2 focus-within:ring-neutral-900/10">
          <Clock className="size-3.5 shrink-0 text-neutral-500" />
          <span className="grid gap-0.5">
            <span className="text-[11px] leading-none text-neutral-500">
              {t("Photoshoot Start")}
            </span>
            <Input
              type="time"
              aria-label={`${t("Photoshoot start time")} — ${slot.label}`}
              value={value}
              className="h-4 w-20 border-0 bg-transparent p-0 text-[13px] leading-none font-bold tabular-nums shadow-none focus-visible:ring-0"
              onBlur={(event) => onChange(timeToMinutes(event.currentTarget.value))}
              onChange={(event) => setValue(event.currentTarget.value)}
            />
          </span>
        </label>
      ) : (
        <ScheduleTimeCard label="Photoshoot Start" value={formatScheduleTime(slot.etaMinutes)} />
      )}
      <ScheduleTimeCard label="Pictures available" value={formatScheduleTime(picturesAvailable)} />
      <ScheduleTimeCard label="Edit deadline" value="—" />
    </div>
  );
}

function TeamScheduleField({
  row,
  canEdit,
  settings,
  onChange,
}: {
  row: BoardRow;
  canEdit: boolean;
  settings: ScheduleSettings;
  onChange: (etaMinutes: number | null) => void;
}) {
  const { t } = useMediaDayStatusLanguage();
  const photoshootStartTime = minutesToTime(row.etaMinutes);
  const picturesAvailableMinutes = addScheduleMinutes(
    row.etaMinutes,
    settings.picturesAvailableDelayMinutes,
  );
  const editPlayerCount = getDeadlinePlayerCount(row);
  const editDeadlineMinutes = addScheduleMinutes(
    picturesAvailableMinutes,
    settings.editDeadlineDelayMinutes * editPlayerCount,
  );
  const [value, setValue] = React.useState(photoshootStartTime);
  const [isEditing, setIsEditing] = React.useState(false);
  const hasValue = Boolean(value);

  const savePhotoshootStart = (event: React.FocusEvent<HTMLInputElement>) => {
    const etaMinutes = timeToMinutes(event.currentTarget.value);
    setIsEditing(false);
    if (!canEdit) return;
    onChange(etaMinutes);
  };

  const picturesAvailable = (
    <ScheduleTimeCard
      label="Pictures available"
      value={formatScheduleTime(picturesAvailableMinutes)}
    />
  );

  const editDeadline = (
    <ScheduleTimeCard label="Edit deadline" value={formatScheduleTime(editDeadlineMinutes)} />
  );

  if (!canEdit) {
    return (
      <div className="flex items-center gap-2">
        <ScheduleTimeCard label="Photoshoot Start" value={formatScheduleTime(row.etaMinutes)} />
        {picturesAvailable}
        {editDeadline}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <label className="inline-flex h-10 min-w-34 items-center gap-2.5 rounded-md border border-neutral-400 bg-white px-3 text-xs font-semibold text-neutral-700 shadow-sm transition-colors focus-within:border-neutral-900 focus-within:ring-2 focus-within:ring-neutral-900/10">
        <Clock className="size-3.5 shrink-0 text-neutral-500" />
        <span className="grid gap-0.5">
          <span className="text-[11px] leading-none font-semibold whitespace-nowrap text-neutral-500">
            {t("Photoshoot Start")}
          </span>
          <span className="relative inline-flex items-center">
            {!hasValue && !isEditing && (
              <span className="pointer-events-none absolute left-0 text-[13px] leading-none font-bold text-neutral-950">
                {t("Set time")}
              </span>
            )}
            <Input
              type="time"
              aria-label={t("Photoshoot start time")}
              value={value}
              className={cn(
                "h-4 w-20 border-0 bg-transparent p-0 text-[13px] leading-none font-bold tabular-nums shadow-none focus-visible:ring-0 [&::-webkit-calendar-picker-indicator]:ml-1 [&::-webkit-calendar-picker-indicator]:opacity-60",
                !hasValue && !isEditing && "text-transparent",
              )}
              onBlur={savePhotoshootStart}
              onChange={(event) => setValue(event.currentTarget.value)}
              onFocus={() => setIsEditing(true)}
            />
          </span>
        </span>
      </label>
      {picturesAvailable}
      {editDeadline}
    </div>
  );
}

function ScheduleTimeCard({ label, value }: { label: string; value: string }) {
  const { t } = useMediaDayStatusLanguage();
  return (
    <div className="inline-flex h-10 min-w-34 items-center gap-2.5 rounded-md border border-neutral-300 bg-white px-3 text-xs font-semibold text-neutral-700 shadow-sm">
      <Clock className="size-3.5 shrink-0 text-neutral-500" />
      <span className="grid gap-0.5">
        <span className="text-[11px] leading-none font-semibold whitespace-nowrap text-neutral-500">
          {t(label)}
        </span>
        <span className="text-[13px] leading-none font-bold text-neutral-950 tabular-nums">
          {value}
        </span>
      </span>
    </div>
  );
}

function PlayerName({ player }: { player: PlayerRow }) {
  const { t } = useMediaDayStatusLanguage();
  const name = player.adjustedName ?? player.name;
  const showOriginal = Boolean(player.adjustedName && player.adjustedName !== player.name);
  const latestReport = player.reports[0];

  return (
    <>
      {player.isCaptain && <Crown className="size-4 shrink-0 text-amber-700" />}
      <span className={cn("truncate", player.isNoShow && "line-through")}>
        {name}
        {showOriginal ? (
          <span className="font-normal text-neutral-500">
            {` (${t("previously")} ${player.name})`}
          </span>
        ) : null}
      </span>
      {latestReport && <StickyNote className="size-4 shrink-0 text-amber-700" />}
    </>
  );
}

function PlayerRole({ player }: { player: PlayerRow }) {
  const { t } = useMediaDayStatusLanguage();
  const role = player.role ?? "—";
  const showOriginal = Boolean(player.originalRole && player.originalRole !== role);

  return (
    <span>
      {t(role)}
      {showOriginal ? (
        <span className="font-normal text-neutral-500">
          {` (${t("previously")} ${t(player.originalRole ?? "")})`}
        </span>
      ) : null}
    </span>
  );
}

function PlayerContextMenu({
  player,
  canEdit,
  canEditCaptain = true,
  onToggleCaptain,
  onAddNote,
  onToggleNoShow,
  className,
  children,
}: {
  player: PlayerRow;
  canEdit: boolean;
  canEditCaptain?: boolean;
  onToggleCaptain: () => void;
  onAddNote: () => void;
  onToggleNoShow: () => void;
  className?: string;
  children: React.ReactNode;
}) {
  const { t } = useMediaDayStatusLanguage();
  const latestReport = player.reports[0];

  return (
    <ContextMenu>
      <ContextMenuTrigger className={cn("block w-full", className)}>
        <PlayerNotesHoverCard player={player}>{children}</PlayerNotesHoverCard>
      </ContextMenuTrigger>
      <ContextMenuContent side="bottom" align="start" className="min-w-48">
        {canEditCaptain ? (
          <>
            <ContextMenuItem disabled={!canEdit || player.isNoShow} onClick={onToggleCaptain}>
              <Crown className="size-4 text-amber-600" />
              {player.isCaptain ? t("Unset captain") : t("Set as captain")}
            </ContextMenuItem>
            <ContextMenuSeparator />
          </>
        ) : null}
        <ContextMenuItem disabled={!canEdit} onClick={onAddNote}>
          <StickyNote className="size-4" />
          {t(player.reports.length ? "Edit notes" : "Add note")}
        </ContextMenuItem>
        <ContextMenuItem disabled={!canEdit} onClick={onToggleNoShow}>
          {player.isNoShow ? (
            <CheckCircle2 className="size-4 text-green-700" />
          ) : (
            <UserX className="size-4 text-destructive" />
          )}
          {player.isNoShow ? t("Unset no show") : t("Set as no show")}
        </ContextMenuItem>
        {!canEdit && (
          <p className="px-2 py-1 text-xs text-muted-foreground">
            {t("Only WorkGrid admins can edit player details.")}
          </p>
        )}
        {latestReport && (
          <p className="max-w-64 px-2 py-1 text-xs whitespace-pre-wrap text-muted-foreground">
            {t("Latest note")}: {latestReport.message}
          </p>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}

const reportSetKinds = ["cutout", "editorial"] as const;

const reportsForSetKind = (reports: PlayerRow["reports"], setKind: WorkflowKind) =>
  reports.filter((report) => !report.setKind || report.setKind === setKind);

function PlayerNotesHoverCard({
  player,
  children,
}: {
  player: PlayerRow;
  children: React.ReactNode;
}) {
  const { t } = useMediaDayStatusLanguage();

  return (
    <HoverCard>
      <HoverCardTrigger
        delay={100}
        closeDelay={100}
        render={<span className="flex w-full cursor-help items-center gap-1.5 truncate" />}
      >
        {children}
      </HoverCardTrigger>
      <HoverCardContent align="start" className="w-80 bg-white text-neutral-950">
        {player.isNoShow ? (
          <div className="grid gap-1.5">
            <p className="text-xs font-semibold tracking-wide text-neutral-500 uppercase">
              {t("No show")}
            </p>
            <p className="text-sm text-neutral-600">{t("Player was absent for the whole day.")}</p>
          </div>
        ) : (
          <div className="grid gap-2">
            <p className="text-xs font-semibold tracking-wide text-neutral-500 uppercase">
              {t("Player notes")}
            </p>
            {reportSetKinds.map((setKind) => (
              <section key={setKind} className="grid gap-1.5">
                <p className="text-[11px] font-semibold tracking-wide text-neutral-500 uppercase">
                  {t(setKind === "cutout" ? "Cutout" : "Editorial")}
                </p>
                {reportsForSetKind(player.reports, setKind).map((report) => (
                  <div
                    key={report.id}
                    className="rounded-md border border-neutral-200 bg-neutral-50 p-2"
                  >
                    <p className="mb-1 text-[11px] font-medium text-neutral-500">
                      {t("Note")}
                      {report.restoredAt ? ` · ${t("restored")}` : ""}
                    </p>
                    <p className="text-sm whitespace-pre-wrap">{report.message}</p>
                  </div>
                ))}
              </section>
            ))}
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}

function PlayerNoteDialog({
  token,
  target,
  onOpenChange,
}: {
  token: string;
  target: { row: BoardRow; player: PlayerRow } | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useMediaDayStatusLanguage();
  const reportPlayerIssue = useMutation(api.ipad.reportPlayerIssue);
  const updatePlayerReport = useMutation(api.ipad.updatePlayerReport).withOptimisticUpdate(
    (localStore, args) =>
      updatePublicBoard(localStore as PublicLocalStore, token, (board) =>
        updateBoardPlayer(board, target?.player.id, (player) => ({
          ...player,
          reports: player.reports.map((report) =>
            String(report.id) === String(args.reportId)
              ? { ...report, message: args.message.trim() }
              : report,
          ),
        })),
      ),
  );
  const deletePlayerReport = useMutation(api.ipad.deletePlayerReport).withOptimisticUpdate(
    (localStore, args) =>
      updatePublicBoard(localStore as PublicLocalStore, token, (board) =>
        updateBoardPlayer(board, target?.player.id, (player) => ({
          ...player,
          reports: player.reports.filter((report) => String(report.id) !== String(args.reportId)),
        })),
      ),
  );
  const player = target?.player;
  const playerName = player?.adjustedName ?? player?.name ?? t("the player");
  const [notes, setNotes] = React.useState<PlayerRow["reports"]>(() => player?.reports ?? []);

  const addNote = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!target) return;
    const form = event.currentTarget;
    const message = String(new FormData(form).get("message") ?? "").trim();
    if (!message) return;

    const reportId = await reportPlayerIssue({
      queueEntryId: target.row.queueEntryId,
      playerId: target.player.id,
      type: "note",
      message,
    });
    setNotes((current) => [
      {
        id: reportId,
        message,
        setKind: target.row.setKind,
        type: "note",
        createdAt: Date.now(),
      },
      ...current,
    ]);
    form.reset();
    toast.success(t("Player note added."));
  };

  const updateNote = async (
    event: React.FormEvent<HTMLFormElement>,
    report: PlayerRow["reports"][number],
  ) => {
    event.preventDefault();
    const message = String(new FormData(event.currentTarget).get("message") ?? "").trim();
    if (!message || message === report.message) return;

    await updatePlayerReport({ reportId: report.id, message });
    setNotes((current) =>
      current.map((item) => (item.id === report.id ? { ...item, message } : item)),
    );
    toast.success(t("Player note updated."));
  };

  const deleteNote = async (report: PlayerRow["reports"][number]) => {
    await deletePlayerReport({ reportId: report.id });
    setNotes((current) => current.filter((item) => item.id !== report.id));
    toast.success(t("Player note deleted."));
  };

  return (
    <Dialog open={Boolean(target)} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="bg-white text-neutral-950 ring-neutral-900/10 sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle>{t("Player notes")}</DialogTitle>
          <DialogDescription>
            {t("Notes for")} {playerName}.{" "}
            {t("They appear as a note indicator on the status board.")}
          </DialogDescription>
        </DialogHeader>

        <div className="grid max-h-[50svh] gap-4 overflow-y-auto pr-1">
          {reportSetKinds.map((setKind) => {
            const reports = reportsForSetKind(notes, setKind);
            return (
              <section key={setKind} className="grid gap-2">
                <p className="text-xs font-semibold tracking-wide text-neutral-500 uppercase">
                  {t(setKind === "cutout" ? "Cutout" : "Editorial")}
                </p>
                {reports.length ? (
                  reports.map((report) => (
                    <form
                      key={report.id}
                      className="grid gap-2 rounded-lg border border-neutral-200 bg-white p-3"
                      onSubmit={(event) => void updateNote(event, report)}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-medium text-muted-foreground">{t("Note")}</p>
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => void deleteNote(report)}
                        >
                          <Trash2 className="size-3.5" /> {t("Delete")}
                        </Button>
                      </div>
                      <Textarea name="message" defaultValue={report.message} />
                      <div className="flex justify-end">
                        <Button type="submit" variant="outline" size="sm">
                          {t("Save changes")}
                        </Button>
                      </div>
                    </form>
                  ))
                ) : (
                  <p className="rounded-lg border border-dashed border-neutral-300 p-3 text-sm text-neutral-500">
                    {t("No notes yet.")}
                  </p>
                )}
              </section>
            );
          })}
        </div>

        <form
          className="grid gap-2 rounded-lg border border-neutral-200 bg-neutral-50 p-3"
          onSubmit={(event) => void addNote(event)}
        >
          <p className="text-xs font-medium text-neutral-500">
            {t("Add another note")} ·{" "}
            {t(target?.row.setKind === "editorial" ? "Editorial" : "Cutout")}
          </p>
          <Textarea
            name="message"
            placeholder={t("What should editors know about this player?")}
            className="bg-white"
          />
          <div className="flex justify-end">
            <Button type="submit">{t("Add note")}</Button>
          </div>
        </form>

        <DialogFooter className="border-neutral-200 bg-neutral-50">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("Close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const stepDescriptions: Record<string, string> = {
  "uploaded-raws": "Retouched and color-graded JPGs, ready for cutout editors to download.",
  "uploaded-jpgs": "Selected editorial JPG or RAW files, ready for editorial editors to download.",
  edited: "High-resolution edited editorial JPG files uploaded by the editorial editors.",
  "editorial-verified":
    "All expected source and edited editorial files are present for people marked Shot, including the team photo when required.",
  "cut-out":
    "Full-body cutout PNGs uploaded by cutout editors. They retain their source dimensions and are not scaled to 4K.",
  cropped:
    "Individual cutouts cropped to the head and torso and scaled to 4K. These are not full-body images.",
  teamcomp:
    "A composite made from players only—no substitutes, coaches, or staff. The captain is centered using the crossed-arms pose; the editor selects varied poses for the remaining players.",
};

function StatusCounters({
  kind,
  rows,
  folderLinks,
}: {
  kind: WorkflowKind;
  rows: BoardRow[];
  folderLinks: Board["folderLinks"];
}) {
  const { language, plural, t } = useMediaDayStatusLanguage();
  const individual = rows.length > 0 && rows.every((row) => row.participantMode === "individual");
  const steps = stepsFor(kind).filter((step) => !individual || step.scope !== "team");
  const players = rows.flatMap((row) =>
    row.players.filter((player) => !player.isNoShow).map((player) => ({ row, player })),
  );
  const playerPhotos = players.reduce(
    (total, { row, player }) => total + expectedPlayerPhotos(kind, row, player),
    0,
  );
  const teamPhotos =
    kind === "editorial"
      ? rows.filter((row) => !row.isSolo && row.players.some((player) => !player.isNoShow)).length
      : 0;
  const plannedPhotos = playerPhotos + teamPhotos;
  const peopleNotShot = players.filter(({ player }) => !player.statuses.shot).length;
  const activeGameId = rows[0]?.gameId;
  const automaticFolderUrl = (stage: (typeof folderLinks.automatic)[number]["stage"]) =>
    folderLinks.automatic.find((folder) => folder.gameId === activeGameId && folder.stage === stage)
      ?.url;
  const folderUrlByStep: Record<string, string | null | undefined> = {
    "uploaded-jpgs": kind === "editorial" ? automaticFolderUrl("editorialSource") : null,
    edited: kind === "editorial" ? automaticFolderUrl("editorialEdited") : null,
    "uploaded-raws":
      kind === "cutout"
        ? (players
            .map(({ player }) => player.automaticVerification.sourceJpg?.openFolderUrl)
            .find(Boolean) ??
          automaticFolderUrl("sourceJpg") ??
          folderLinks.raws)
        : null,
    "cut-out":
      players
        .map(({ player }) => player.automaticVerification.cutout?.openFolderUrl)
        .find(Boolean) ?? automaticFolderUrl("cutout"),
    cropped:
      players
        .map(({ player }) => player.automaticVerification.cropped?.openFolderUrl)
        .find(Boolean) ?? automaticFolderUrl("cropped"),
    teamcomp:
      rows.map((row) => row.automaticVerification.teamcomp?.openFolderUrl).find(Boolean) ??
      automaticFolderUrl("teamcomp"),
  };
  const progress = steps.map((step) => {
    const applicableRows = rows.filter((row) => step.applicable?.(row) ?? true);
    const applicablePlayers = players.filter(
      ({ row, player }) => step.applicable?.(row, player) ?? true,
    );
    const teamPhotoRows =
      kind === "editorial" && step.scope !== "team"
        ? applicableRows.filter(
            (row) => !row.isSolo && row.players.some((player) => !player.isNoShow),
          )
        : [];
    const total =
      step.scope === "team"
        ? applicableRows.length
        : applicablePlayers.reduce(
            (count, { row, player }) => count + expectedPlayerPhotos(kind, row, player),
            teamPhotoRows.length,
          );
    const done =
      step.scope === "team"
        ? applicableRows.filter((row) => step.done(row)).length
        : applicablePlayers.reduce(
            (count, { row, player }) =>
              count + (step.done(row, player) ? expectedPlayerPhotos(kind, row, player) : 0),
            teamPhotoRows.filter((row) => {
              const applicable = row.players.filter(
                (player) => !player.isNoShow && (step.applicable?.(row, player) ?? true),
              );
              return step.teamDone
                ? step.teamDone(row)
                : applicable.length > 0 && applicable.every((player) => step.done(row, player));
            }).length,
          );
    return {
      step,
      done,
      total,
      percent: total ? Math.round((done / total) * 100) : 0,
    };
  });

  return (
    <div className="rounded-md border border-neutral-300 bg-white px-3 py-4 text-neutral-950 shadow-sm md:px-5 md:py-5">
      <div className="mb-4 flex items-center gap-3 border-b border-neutral-200 pb-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-full border border-neutral-300 bg-neutral-50 text-neutral-700">
          <Images className="size-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-bold tracking-wide text-neutral-500 uppercase">
            {t("Planned photos")}
          </div>
          <div className="truncate text-xs font-medium text-neutral-600">
            {kind === "editorial"
              ? `${playerPhotos} ${plural(playerPhotos, language === "pl" ? ["gracz", "graczy", "graczy"] : ["player", "players", "players"])} · ${teamPhotos} ${plural(teamPhotos, language === "pl" ? ["team", "teamy", "teamów"] : ["team", "teams", "teams"])}`
              : `${playerPhotos} ${t("across all people")}`}
          </div>
        </div>
        <div className="text-3xl font-black text-neutral-950 tabular-nums">{plannedPhotos}</div>
      </div>
      <div className="relative">
        {progress.slice(0, -1).map(({ step, percent, total }, index) => {
          const complete = percent === 100 && total > 0;

          return (
            <div
              key={`${step.key}-connector`}
              className="absolute top-[17px] hidden h-2.5 overflow-hidden rounded-full bg-neutral-200 shadow-inner md:block"
              style={{
                left: `calc((100% / ${progress.length}) * ${index} + (100% / ${progress.length}) / 2)`,
                width: `calc(100% / ${progress.length})`,
              }}
            >
              <div
                className={cn(
                  "h-full rounded-full transition-all",
                  complete ? "bg-green-500" : "bg-orange-400",
                )}
                style={{ width: `${percent}%` }}
              />
            </div>
          );
        })}

        <div
          className={cn(
            "relative z-10 grid w-full grid-cols-2 items-start gap-y-5 md:gap-y-0",
            kind === "cutout" ? "md:grid-cols-6" : "md:grid-cols-4",
          )}
        >
          {progress.map(({ step, done, total, percent }) => {
            const complete = percent === 100 && total > 0;
            const Icon = complete ? Check : step.icon;
            const downloadStep = step.key === "uploaded-raws" || step.key === "uploaded-jpgs";
            const folderAction = t(downloadStep ? "Download photos" : "Upload photos");
            const FolderActionIcon = downloadStep ? Download : Upload;

            return (
              <div key={step.key} className="flex min-w-0 flex-col items-center text-center">
                <div
                  className={cn(
                    "flex size-9 items-center justify-center rounded-full border-2 bg-white shadow-sm transition-colors md:size-11",
                    complete
                      ? "border-green-600 bg-green-50 text-green-700"
                      : percent > 0
                        ? "border-orange-400 bg-orange-50 text-orange-700"
                        : "border-neutral-300 text-neutral-600",
                  )}
                >
                  <Icon className="size-4 md:size-5" />
                </div>
                <div className="mt-2 flex items-center justify-center gap-1 px-1 text-xs leading-tight font-semibold text-neutral-900 md:mt-3 md:text-[13px] md:whitespace-nowrap">
                  {stepDescriptions[step.key] ? (
                    <HoverCard>
                      <HoverCardTrigger
                        delay={150}
                        closeDelay={100}
                        render={
                          <span
                            tabIndex={0}
                            aria-label={`${t("About")} ${t(String(step.label))}`}
                            className="inline-flex cursor-help items-center gap-1 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-neutral-500"
                          />
                        }
                      >
                        <span>{t(String(step.label))}</span>
                        <CircleHelp className="size-3.5 shrink-0 text-neutral-500" />
                      </HoverCardTrigger>
                      <HoverCardContent
                        align="center"
                        side="bottom"
                        className="w-72 bg-white p-3 text-left text-neutral-950"
                      >
                        <p className="text-sm font-semibold">{t(String(step.label))}</p>
                        <p className="mt-1 text-xs leading-relaxed text-neutral-600">
                          {t(stepDescriptions[step.key] ?? "")}
                        </p>
                      </HoverCardContent>
                    </HoverCard>
                  ) : (
                    <span>{t(String(step.label))}</span>
                  )}
                </div>
                <div className="mt-1 text-sm font-semibold text-neutral-500 tabular-nums">
                  {done} / {total}
                  <span className="ml-1 text-[10px] font-medium tracking-wide uppercase">
                    {step.scope === "team"
                      ? plural(
                          total,
                          language === "pl"
                            ? ["team", "teamy", "teamów"]
                            : ["team", "teams", "teams"],
                        )
                      : plural(
                          total,
                          language === "pl"
                            ? ["zdjęcie", "zdjęcia", "zdjęć"]
                            : ["photo", "photos", "photos"],
                        )}
                  </span>
                </div>
                {step.key === "verified" || step.key === "editorial-verified" ? (
                  <div className="mt-1 text-[10px] font-semibold tracking-wide text-neutral-500 uppercase">
                    {peopleNotShot}{" "}
                    {plural(
                      peopleNotShot,
                      language === "pl"
                        ? ["osoba", "osoby", "osób"]
                        : ["person", "people", "people"],
                    )}{" "}
                    {t("not shot")}
                  </div>
                ) : null}
                {folderUrlByStep[step.key] ? (
                  <Button
                    size="sm"
                    variant="default"
                    className="mt-2 h-7 gap-1 !bg-neutral-950 px-2 text-[11px] !text-white shadow-sm hover:!bg-neutral-800 hover:!text-white"
                    nativeButton={false}
                    render={
                      <a
                        href={folderUrlByStep[step.key] ?? undefined}
                        target="_blank"
                        rel="noreferrer"
                      />
                    }
                  >
                    {folderAction} <FolderActionIcon className="size-3" />
                  </Button>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function RemoteEditorField({
  token,
  queueEntryId,
  value,
  disabled,
}: {
  token: string;
  queueEntryId: BoardRow["queueEntryId"];
  value: string;
  disabled: boolean;
}) {
  const { t } = useMediaDayStatusLanguage();
  const updateEditorText = useMutation(
    api.mediaDayStatus.updatePublicRemoteEditorText,
  ).withOptimisticUpdate((localStore, args) =>
    updatePublicBoard(localStore as PublicLocalStore, token, (board) =>
      updateBoardRow(board, args.queueEntryId, (row) => ({
        ...row,
        remoteEditorText: args.editorText.trim(),
      })),
    ),
  );

  return (
    <RemoteTextField
      value={value}
      disabled={disabled}
      placeholder={t("Unassigned")}
      disabledTitle={t("Only WorkGrid admins can edit the editor")}
      onSave={(editorText) => updateEditorText({ token, queueEntryId, editorText })}
    />
  );
}

function CropperField({
  token,
  queueEntryId,
  value,
  disabled,
}: {
  token: string;
  queueEntryId: BoardRow["queueEntryId"];
  value: string;
  disabled: boolean;
}) {
  const { t } = useMediaDayStatusLanguage();
  const updateCropperText = useMutation(
    api.mediaDayStatus.updatePublicCropperText,
  ).withOptimisticUpdate((localStore, args) =>
    updatePublicBoard(localStore as PublicLocalStore, token, (board) =>
      updateBoardRow(board, args.queueEntryId, (row) => ({
        ...row,
        cropperText: args.cropperText.trim(),
      })),
    ),
  );

  return (
    <RemoteTextField
      value={value}
      disabled={disabled}
      placeholder={t("Unassigned")}
      disabledTitle={t("Only WorkGrid admins can edit the cropper")}
      onSave={(cropperText) => updateCropperText({ token, queueEntryId, cropperText })}
    />
  );
}

function RemoteTextField({
  value,
  disabled,
  placeholder,
  disabledTitle,
  onSave,
}: {
  value: string;
  disabled: boolean;
  placeholder: string;
  disabledTitle: string;
  onSave: (value: string) => Promise<unknown>;
}) {
  const save = (event: React.FocusEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const nextValue = input.value.trim();
    if (disabled || nextValue === value) return;

    void onSave(nextValue).catch(() => {
      input.value = value;
    });
  };

  return (
    <Input
      key={value}
      defaultValue={value}
      disabled={disabled}
      maxLength={200}
      placeholder={placeholder}
      title={disabled ? disabledTitle : undefined}
      className="h-7 rounded-sm border-neutral-300 bg-white px-2 text-xs shadow-none disabled:pointer-events-none disabled:bg-neutral-100 disabled:text-neutral-600 disabled:opacity-100"
      onBlur={save}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur();
      }}
    />
  );
}

function StatusMark({
  token,
  row,
  player,
  step,
  checked,
  disabled,
  showHistory,
}: {
  token: string;
  row: BoardRow;
  player: PlayerRow;
  step: Step;
  checked: boolean;
  disabled: boolean;
  showHistory: boolean;
}) {
  const { t } = useMediaDayStatusLanguage();
  const updateStatus = useMutation(api.mediaDayStatus.updatePublicStatus).withOptimisticUpdate(
    (localStore, args) =>
      updatePublicBoard(localStore as PublicLocalStore, token, (board) =>
        updateBoardRow(board, args.queueEntryId, (cachedRow) => {
          const players = cachedRow.players.map((cachedPlayer) =>
            String(cachedPlayer.id) === String(args.playerId)
              ? {
                  ...cachedPlayer,
                  statuses: { ...cachedPlayer.statuses, [args.stepKey]: args.checked },
                  isShot: args.stepKey === "shot" ? args.checked : cachedPlayer.isShot,
                }
              : cachedPlayer,
          );
          const playersShot = players.filter((cachedPlayer) => cachedPlayer.statuses.shot).length;
          return {
            ...cachedRow,
            players,
            playersShot,
            shot: players.length > 0 && playersShot === players.length,
          };
        }),
      ),
  );

  return (
    <StatusHistoryHover row={row} player={player} kind={row.setKind} enabled={showHistory}>
      <Checkbox
        checked={checked}
        aria-label={`${t(String(step.label))}: ${t(checked ? "done" : "pending")}`}
        className="mx-auto size-4 rounded-[3px] border-green-900/60 bg-white/40 text-green-950 data-checked:border-green-900 data-checked:bg-green-600 data-checked:text-green-950"
        disabled={disabled}
        title={
          disabled
            ? step.verificationControlled
              ? t("Automatically controlled by Voxis verification")
              : t("Only WorkGrid admins can change this status")
            : undefined
        }
        onCheckedChange={
          disabled
            ? undefined
            : (nextChecked) =>
                updateStatus({
                  token,
                  queueEntryId: row.queueEntryId,
                  playerId: player.id,
                  setKind: row.setKind,
                  stepKey: step.key,
                  checked: nextChecked === true,
                })
        }
      />
    </StatusHistoryHover>
  );
}

function TeamStatusMark({
  token,
  row,
  step,
  checked,
  disabled,
  showHistory,
}: {
  token: string;
  row: BoardRow;
  step: Step;
  checked: boolean;
  disabled: boolean;
  showHistory: boolean;
}) {
  const { t } = useMediaDayStatusLanguage();
  const updateTeamStatus = useMutation(
    api.mediaDayStatus.updatePublicTeamStatus,
  ).withOptimisticUpdate((localStore, args) =>
    updatePublicBoard(localStore as PublicLocalStore, token, (board) =>
      updateBoardRow(board, args.queueEntryId, (cachedRow) => ({
        ...cachedRow,
        teamStatuses: { ...cachedRow.teamStatuses, [args.stepKey]: args.checked },
        players: cachedRow.players.map((cachedPlayer) => ({
          ...cachedPlayer,
          statuses: { ...cachedPlayer.statuses, [args.stepKey]: args.checked },
        })),
      })),
    ),
  );

  return (
    <StatusHistoryHover row={row} kind={row.setKind} enabled={showHistory}>
      <Checkbox
        checked={checked}
        aria-label={`${t(String(step.label))} ${t("team status")}: ${t(checked ? "done" : "pending")}`}
        className="size-4 rounded-[3px] border-green-900/60 bg-white/40 text-green-950 data-checked:border-green-900 data-checked:bg-green-600 data-checked:text-green-950"
        disabled={disabled}
        title={
          disabled
            ? step.verificationControlled
              ? t("Automatically controlled by Voxis verification")
              : t("This team status cannot be changed")
            : undefined
        }
        onCheckedChange={
          disabled
            ? undefined
            : (nextChecked) =>
                updateTeamStatus({
                  token,
                  queueEntryId: row.queueEntryId,
                  setKind: row.setKind,
                  stepKey: step.key as "teamcomp",
                  checked: nextChecked === true,
                })
        }
      />
    </StatusHistoryHover>
  );
}

function StatusHistoryHover({
  row,
  player,
  kind,
  enabled,
  children,
}: {
  row: BoardRow;
  player?: PlayerRow;
  kind: WorkflowKind;
  enabled: boolean;
  children: React.ReactNode;
}) {
  if (!enabled) return children;

  return (
    <HoverCard>
      <HoverCardTrigger render={<span className="inline-flex items-center justify-center" />}>
        {children}
      </HoverCardTrigger>
      <HoverCardContent align="center" className="w-80 bg-white text-neutral-950">
        <StatusHistoryCard row={row} player={player} kind={kind} />
      </HoverCardContent>
    </HoverCard>
  );
}

function StatusHistoryCard({
  row,
  player,
  kind,
}: {
  row: BoardRow;
  player?: PlayerRow;
  kind: WorkflowKind;
}) {
  const { locale, t } = useMediaDayStatusLanguage();
  const subject = player ? (player.adjustedName ?? player.name) : row.teamName;
  const rows = stepsFor(kind)
    .filter((step) => step.applicable?.(row, player) ?? true)
    .map((step) => {
      const done = step.scope === "team" ? step.done(row) : player ? step.done(row, player) : false;
      const updatedAt =
        step.scope === "team"
          ? row.teamStatusUpdatedAt?.[step.key as keyof typeof row.teamStatusUpdatedAt]
          : player?.statusUpdatedAt?.[step.key as keyof typeof player.statusUpdatedAt];

      return { step, done, updatedAt };
    });

  return (
    <div className="grid gap-3">
      <div>
        <p className="text-xs font-semibold tracking-wide text-neutral-500 uppercase">
          {t("Status history")}
        </p>
        <p className="mt-0.5 truncate text-sm font-bold text-neutral-950">{subject}</p>
        <p className="truncate text-xs text-neutral-500">
          {row.gameName} · {row.setName}
        </p>
      </div>
      <div className="grid gap-1.5">
        {rows.map(({ step, done, updatedAt }) => (
          <div
            key={step.key}
            className="flex items-center justify-between gap-3 rounded-md border border-neutral-200 bg-neutral-50 px-2 py-1.5"
          >
            <span className="min-w-0 truncate text-xs font-semibold text-neutral-800">
              {t(String(step.label))}
            </span>
            <span
              className={cn(
                "shrink-0 text-xs font-semibold tabular-nums",
                done ? "text-green-700" : "text-neutral-500",
              )}
            >
              {formatStatusTime(done, updatedAt, locale, t)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatStatusTime(
  done: boolean,
  updatedAt: number | null | undefined,
  locale: string,
  t: (phrase: string) => string,
) {
  if (!done) return t("Pending");
  if (!updatedAt) return t("Done");

  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(updatedAt));
}

function stepsFor(kind: WorkflowKind): Step[] {
  if (kind === "editorial") {
    return [
      {
        key: "shot",
        label: "Shot",
        icon: Camera,
        adminOnly: true,
        done: (_row, player) => player?.statuses.shot === true,
      },
      {
        key: "uploaded-jpgs",
        label: "Download Ready",
        icon: Download,
        adminOnly: true,
        verificationControlled: true,
        applicable: (row, player) =>
          player ? player.statuses.shot === true : row.players.some((item) => item.statuses.shot),
        teamDone: (row) => row.automaticVerification.editorialSource?.complete === true,
        done: (row, player) =>
          player?.statuses["uploaded-jpgs"] === true &&
          (row.isSolo ||
            String(row.players[0]?.id) !== String(player.id) ||
            row.automaticVerification.editorialSource?.complete === true),
      },
      {
        key: "edited",
        label: "Edited",
        icon: Pencil,
        verificationControlled: true,
        applicable: (row, player) =>
          player ? player.statuses.shot === true : row.players.some((item) => item.statuses.shot),
        teamDone: (row) => row.automaticVerification.editorialEdited?.complete === true,
        done: (_row, player) => player?.statuses.edited === true,
      },
      {
        key: "editorial-verified",
        label: "Verified",
        icon: CheckCircle2,
        automatic: true,
        applicable: (row, player) =>
          player ? player.statuses.shot === true : row.players.some((item) => item.statuses.shot),
        teamDone: (row) =>
          row.automaticVerification.editorialSource?.complete === true &&
          row.automaticVerification.editorialEdited?.complete === true,
        done: (row, player) => {
          const items = [
            player?.automaticVerification.editorialSource,
            player?.automaticVerification.editorialEdited,
            ...(row.isSolo || String(row.players[0]?.id) !== String(player?.id)
              ? []
              : [
                  row.automaticVerification.editorialSource,
                  row.automaticVerification.editorialEdited,
                ]),
          ];
          return items.length > 0 && items.every((item) => item?.status === "verified");
        },
      },
    ];
  }

  return [
    {
      key: "shot",
      label: "Shot",
      icon: Camera,
      adminOnly: true,
      done: (_row, player) => player?.statuses.shot === true,
    },
    {
      key: "uploaded-raws",
      label: "Download Ready",
      icon: Download,
      verificationControlled: true,
      adminOnly: true,
      done: (_row, player) => player?.statuses["uploaded-raws"] === true,
    },
    {
      key: "cut-out",
      label: "Cut out",
      icon: Scissors,
      verificationControlled: true,
      done: (_row, player) => player?.statuses["cut-out"] === true,
    },
    {
      key: "cropped",
      label: "Cropped",
      icon: Crop,
      verificationControlled: true,
      done: (_row, player) => player?.statuses.cropped === true,
    },
    {
      key: "teamcomp",
      label: "Teamcomp",
      icon: Images,
      scope: "team",
      verificationControlled: true,
      applicable: (row) =>
        !row.isSolo && row.players.some((player) => !player.isNoShow && isTeamcompPlayer(player)),
      done: (row) => row.teamStatuses.teamcomp,
    },
    {
      key: "verified",
      label: "Verified",
      icon: CheckCircle2,
      automatic: true,
      applicable: (_row, player) => player?.statuses.shot === true,
      done: (row, player) => {
        const items = [
          player?.automaticVerification.sourceJpg,
          player?.automaticVerification.cutout,
          player?.automaticVerification.cropped,
          ...(row.isSolo ? [] : [row.automaticVerification.teamcomp]),
        ];
        return items.length > 0 && items.every((item) => item?.status === "verified");
      },
    },
  ];
}

function getSetTabs(rows: BoardRow[], slots: BoardSlot[]): SetTab[] {
  const sets = new Map<string, SetTab & { stageOrder: number }>();
  for (const item of [...rows, ...slots]) {
    const key = setKey(item);
    const current = sets.get(key) ?? {
      key,
      label: item.setName,
      kind: item.setKind,
      gameNames: [] as string[],
      participantMode: item.participantMode,
      teams: 0,
      done: 0,
      assigneeProgress: {} as Record<string, SetAssigneeProgress>,
      stageOrder: item.stageOrder,
    };
    current.teams += 1;
    if ("shot" in item) {
      current.done += item.shot ? 1 : 0;
      if (!current.gameNames.includes(item.gameName)) current.gameNames.push(item.gameName);
      for (const person of getRowAssignees(item)) {
        const progress = (current.assigneeProgress[person.key] ??= {});
        const roleProgress = (progress[person.role] ??= { done: 0, total: 0 });
        roleProgress.total += 1;
        roleProgress.done += isRowRoleDone(item, person.role) ? 1 : 0;
      }
    }
    sets.set(key, current);
  }

  return [...sets.values()].toSorted(compareSetTabs).map((set) => ({
    key: set.key,
    label: set.label,
    kind: set.kind,
    gameNames: set.gameNames,
    participantMode: set.participantMode,
    teams: set.teams,
    done: set.done,
    assigneeProgress: set.assigneeProgress,
  }));
}

function filterRowsBySet(rows: BoardRow[], key: string) {
  return key === "all" ? rows : rows.filter((row) => setKey(row) === key);
}

function compareSetTabs(
  a: { kind: WorkflowKind; label: string; stageOrder: number },
  b: { kind: WorkflowKind; label: string; stageOrder: number },
) {
  return (
    setKindRank(a.kind) - setKindRank(b.kind) ||
    setNumber(a.label) - setNumber(b.label) ||
    a.stageOrder - b.stageOrder ||
    a.label.localeCompare(b.label)
  );
}

function compareRowsBySet(a: BoardRow, b: BoardRow) {
  return (
    compareSetTabs(
      { kind: a.setKind, label: a.setName, stageOrder: a.stageOrder },
      { kind: b.setKind, label: b.setName, stageOrder: b.stageOrder },
    ) ||
    a.gameName.localeCompare(b.gameName) ||
    a.order - b.order ||
    a.teamName.localeCompare(b.teamName)
  );
}

function setKindRank(kind: WorkflowKind) {
  return kind === "cutout" ? 0 : 1;
}

function setNumber(label: string) {
  const match = label.match(/\d+/);
  return match ? Number(match[0]) : Number.MAX_SAFE_INTEGER;
}

function gameKey(gameName: string) {
  return gameName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-");
}

function setKey(row: Pick<BoardRow, "stageOrder" | "setName">) {
  return `${row.stageOrder}:${gameKey(row.setName)}`;
}

function getDeadlinePlayerCount(row: BoardRow) {
  return row.players.filter((player) => !player.isNoShow && !isEditorialRoleHidden(player.role))
    .length;
}

function isTeamcompPlayer(player: PlayerRow) {
  return player.role?.trim() === "Player";
}

function groupRowsByTeam(rows: BoardRow[], kind: WorkflowKind) {
  return rows
    .map((row) => {
      const teamcompPlayerCount = row.players.filter(
        (player) => !player.isNoShow && isTeamcompPlayer(player),
      ).length;
      const teamcompRank = (player: PlayerRow) =>
        isTeamcompPlayer(player) ? (player.isNoShow ? 1 : 0) : 2;
      const players =
        kind === "cutout"
          ? row.players.toSorted((a, b) => teamcompRank(a) - teamcompRank(b))
          : row.players;

      return { row, players, teamcompPlayerCount };
    })
    .toSorted((a, b) => a.row.order - b.row.order || a.row.teamName.localeCompare(b.row.teamName));
}

function groupRowsBySection(rows: BoardRow[]) {
  const sections = [
    ...rows
      .reduce((acc, row) => {
        const key = `${row.setName}:${row.gameName}`;
        const current = acc.get(key) ?? { key, label: sectionLabel(row), rows: [] as BoardRow[] };
        current.rows.push(row);
        acc.set(key, current);
        return acc;
      }, new Map<string, { key: string; label: string; rows: BoardRow[] }>())
      .values(),
  ].toSorted((a, b) => {
    const firstA = a.rows[0];
    const firstB = b.rows[0];
    if (!firstA || !firstB) return a.label.localeCompare(b.label);
    return compareRowsBySet(firstA, firstB) || a.label.localeCompare(b.label);
  });

  return sections.map((section) => ({
    key: section.key,
    label: section.label,
    rows: section.rows,
    showHeader: sections.length > 1,
  }));
}

function sectionLabel(row: BoardRow) {
  return `${row.setName} · ${row.gameName}`;
}

function formatBoardDate(date: string, locale: string) {
  return new Date(`${date}T00:00:00`).toLocaleDateString(locale, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function sum(rows: BoardRow[], key: "playersShot" | "playersTotal") {
  return rows.reduce((total, row) => total + row[key], 0);
}

function getRowsForKind(rows: BoardRow[], kind: WorkflowKind, showAllRoles = false) {
  const visibleRows = rows
    .filter((row) => row.setKind === kind)
    .map((row) => {
      const eligiblePlayers = row.players.filter(
        (player) =>
          !player.isNoShow && (kind !== "editorial" || !isEditorialRoleHidden(player.role)),
      );
      const players = kind === "editorial" && !showAllRoles ? eligiblePlayers : row.players;

      return {
        ...row,
        players,
        playersShot: eligiblePlayers.filter((player) => player.statuses.shot).length,
        playersTotal: eligiblePlayers.length,
        shot: eligiblePlayers.length > 0 && eligiblePlayers.every((player) => player.statuses.shot),
      };
    });

  return visibleRows.toSorted(compareRowsBySet);
}

function expectedPlayerPhotos(kind: WorkflowKind, row: BoardRow, player: PlayerRow) {
  if (player.isNoShow) return 0;
  if (kind === "editorial") return isEditorialRoleHidden(player.role) ? 0 : 3;

  const teamPhoto = row.participantMode === "individual" || row.isSolo ? 0 : 1;
  const playerFiles = Math.max(player.verificationExpectedFiles - teamPhoto, 0);
  return Math.round(playerFiles / 3);
}
